import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import StudentDetailsModal from '../../components/StudentDetailsModal';
import ClearDataModal from '../../components/ClearDataModal';
import { getImageURL } from '../../utils/api';
import Swal from 'sweetalert2';
import Select from 'react-select';
import Cropper from 'react-easy-crop';
import * as XLSX from 'xlsx';
import { STUDENT_TEMPLATE_COLUMNS } from '../../constants/studentTemplate';

const REGISTRATION_STATUS_OPTIONS = [
    { value: 'active', valueLabel: 'active' },
    { value: 'postponed', valueLabel: 'postponed' },
    { value: 'pending', valueLabel: 'pending' },
    { value: 'suspending', valueLabel: 'suspending' },
    { value: 'inactivating', valueLabel: 'inactivating' },
    { value: 'inactivated', valueLabel: 'inactivated' },
    { value: 'suspended', valueLabel: 'suspended' }
].map((o) => ({ value: o.value, label: o.valueLabel }));

const DELIVERY_STATUS_OPTIONS = [
    { value: '', valueLabel: 'Active (Ongoing)' },
    { value: 'academic delivery completed', valueLabel: 'academic delivery completed' },
    { value: 'graduated', valueLabel: 'graduated' },
    { value: 'certificate received', valueLabel: 'certificate received' },
    { value: 'certificate dispatched', valueLabel: 'certificate dispatched' },
    { value: 'Dropped', valueLabel: 'Dropped' },
    { value: 'Transfer to another branch', valueLabel: 'Transfer to another branch' },
    { value: 'Transfer to another course', valueLabel: 'Transfer to another course' }
].map((o) => ({ value: o.value, label: o.valueLabel }));

const STREAM_TEMPLATE_OPTIONS = ['Software Engineering', 'Network Engineering'];
const DELIVERY_STATUS_TEMPLATE_OPTIONS = [
    'academic delivery completed',
    'graduated',
    'certificate received',
    'certificate dispatched',
    'Dropped',
    'Transfer to another branch',
    'Transfer to another course'
];

const getRegistrationStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'active':
            return 'bg-green-100 text-green-800';
        case 'completed':
            return 'bg-blue-100 text-blue-800';
        case 'dropped':
            return 'bg-red-100 text-red-800';
        case 'postponed':
            return 'bg-amber-100 text-amber-800';
        case 'pending':
            return 'bg-slate-100 text-slate-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="260" viewBox="0 0 200 260"><rect width="200" height="260" fill="%23f1f5f9"/><text x="50%" y="50%" text-anchor="middle" fill="%2394a3b8" font-size="20" font-family="Arial, sans-serif">No Photo</text></svg>';

// Helper function to create image from crop
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

// Image Upload Modal Component
export function ImageUploadModal({ student, onClose, onSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCurrentImage, setShowCurrentImage] = useState(Boolean(student?.imagePath));
  const fileInputRef = useRef(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle clipboard paste
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Check if the clipboard item is an image
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          
          if (file) {
            if (file.size > 10 * 1024 * 1024) {
              Swal.fire('Error', 'File size must be less than 10MB', 'error');
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              setImageSrc(reader.result);
              setSelectedFile(file);
              setShowCurrentImage(false);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'File size must be less than 10MB', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setSelectedFile(file);
        setShowCurrentImage(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      Swal.fire('Error', 'Please select and crop an image', 'error');
      return;
    }

    try {
      setUploading(true);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Create form data
      const formData = new FormData();
      formData.append('image', croppedBlob, selectedFile.name);

      // Upload to backend (trim studentId to remove any spaces)
      const response = await axiosInstance.post(
        `/students/${student.studentId.trim()}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        Swal.fire('Success', 'Student image uploaded successfully', 'success');
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white shadow-2xl">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">
                                {showCurrentImage ? 'Current Student Photo' : 'Upload Student Photo'}
                            </h2>
                            <p className="mt-1 text-sm text-slate-600">
                                {student.fullName} ({student.studentId})
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-200"
                            disabled={uploading}
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    {showCurrentImage && student?.imagePath ? (
                        <div className="text-center">
                            <div className="relative inline-block">
                                <img
                                    src={getImageURL(student.imagePath)}
                                    alt={student.fullName}
                                    className="max-h-96 rounded-lg border-2 border-slate-200 shadow-lg"
                                    onError={() => setShowCurrentImage(false)}
                                />
                            </div>
                            <div className="mt-6 flex justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCurrentImage(false);
                                        fileInputRef.current?.click();
                                    }}
                                    className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-600 bg-slate-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:border-slate-700 hover:bg-slate-700"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Replace Photo
                                </button>
                            </div>
                        </div>
                    ) : !imageSrc ? (
                        <div className="text-center">
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 p-12 transition-colors hover:border-slate-400 hover:bg-slate-50"
                            >
                                <svg
                                    className="mx-auto h-16 w-16 text-slate-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                                <p className="mt-4 text-lg font-semibold text-slate-700">Click to select image</p>
                                <p className="mt-2 text-sm text-slate-500">JPEG, PNG, or WEBP (max 10MB)</p>
                                <p className="mt-2 text-xs text-slate-400">or press Ctrl+V to paste from clipboard</p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/jpg,image/webp"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                            {student?.imagePath && (
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentImage(true)}
                                    className="mt-4 text-sm text-slate-600 underline hover:text-slate-800"
                                >
                                    View current photo
                                </button>
                            )}
                        </div>
                    ) : (
                        <div>
                            <div className="relative h-96 overflow-hidden rounded-lg bg-black">
                                <Cropper
                                    image={imageSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={3 / 4}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                />
                            </div>

                            <div className="mt-4">
                                <label className="mb-2 block text-sm font-semibold text-slate-700">Zoom</label>
                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-slate-200"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    setImageSrc(null);
                                    setSelectedFile(null);
                                    setCrop({ x: 0, y: 0 });
                                    setZoom(1);
                                }}
                                className="mt-4 text-sm text-slate-600 underline hover:text-slate-800"
                                disabled={uploading}
                            >
                                Select different image
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={uploading}
                        className="rounded-lg border-2 border-slate-300 bg-white px-6 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {!showCurrentImage && (
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={!imageSrc || uploading}
                            className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-600 bg-slate-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:border-slate-700 hover:bg-slate-700 disabled:opacity-50"
                        >
                            {uploading ? (
                                <>
                                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Upload Photo
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Helper: show only the base registration number (before any "-" suffix)
const formatRegistrationNumberBase = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    return raw.split('-')[0].trim();
};

// Print Modal Component with Tabs
export function PrintModal({ student, onClose }) {
    const [activeTab, setActiveTab] = useState('letters');
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [assignedCourseIds, setAssignedCourseIds] = useState([]);
    const [coursesLoaded, setCoursesLoaded] = useState(false);
    const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);
        const [assignmentNoticeType, setAssignmentNoticeType] = useState('assignment-submission');
        const [parentMeetingDate, setParentMeetingDate] = useState('');

  const tabs = [
    { id: 'letters', label: 'Letters', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { id: 'warning', label: 'Warning Letter', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
    { id: 'recommendation', label: 'Recommendation Letter', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        { id: 'registered-post', label: 'Registered Post', icon: 'M4 6h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm0 0l8 5 8-5' },
    { id: 'behavior', label: 'Erroneous Behavior', icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' }
  ];

    const registrations = student.registrations || [];

    const selectedRegistration = useMemo(
        () => registrations.find((r) => r.registrationId === selectedRegistrationId) || null,
        [registrations, selectedRegistrationId]
    );

    const showHndPathwayLetter = useMemo(() => {
        if (!selectedRegistration?.courseName) return false;
        const lower = selectedRegistration.courseName.toLowerCase();
        return lower.includes('hnd') && lower.includes('comput');
    }, [selectedRegistration]);

    useEffect(() => {
        const fetchAssignedCourses = async () => {
            try {
                const response = await axiosInstance.get('/admin/me/coordinate-courses');
                const coordinateCourses = response.data?.coordinateCourses || {};

                const editableCourseIds = Object.values(coordinateCourses)
                    .filter((c) => c.isUpdate || c.isDelete)
                    .map((c) => Number(c.courseId));

                setAssignedCourseIds(editableCourseIds);
            } catch (err) {
                console.error('Failed to fetch coordinate courses for print modal:', err);
            } finally {
                setCoursesLoaded(true);
            }
        };

        fetchAssignedCourses();
    }, []);

    const coordinatedRegistrations = coursesLoaded
        ? registrations.filter((reg) =>
                reg.courseId ? assignedCourseIds.includes(Number(reg.courseId)) : false
            )
        : [];

    const currentUser = AuthService.getUser();

    const openFollowingLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const nic = student.nicNumber || 'N/A';
        const courseName = reg.courseName || 'Course';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';
        const pearsonId = reg.pearsonId || '';
        const universityId = student.universityId || student.universityID || '';
        const location = 'Batticaloa';

        const userName = currentUser?.superAdmin?.staff?.name
            || '';
        const userTitle = currentUser?.superAdmin?.staff?.title
            || currentUser?.title
            || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName
            || currentUser?.superAdmin?.staff?.designation
            || '';

        const gender = (student.gender || '').toString().toLowerCase();
        let titlePrefix = '';
        if (gender === 'male' || gender === 'm') titlePrefix = 'Mr. ';
        else if (gender === 'female' || gender === 'f') titlePrefix = 'Ms. ';

        const refLineName = `${titlePrefix}${name}`.trim();

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Following Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>

    <br /><br />
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4">
        <span class="label">Ref:</span> ${refLineName} (NIC No: ${nic})
    </div>

    <div class="mt-4 justify">
        This is to certify that <strong>${refLineName}</strong> is a student of <strong>ESOFT Metro Campus</strong> and has been following the course titled <strong>${courseName}</strong> at <strong>ESOFT Metro Campus</strong>. The medium of instruction was <strong>English</strong>.
    </div>

    <div class="mt-2"><span class="label">Studied Location:</span> ${location}</div>

    <div class="mt-2"><span class="label">ESOFT Registration ID:</span> ${regNumber}</div>

    ${universityId ? `<div class="mt-2"><span class="label">University ID:</span> ${universityId}</div>` : ''}
    ${pearsonId ? `<div class="mt-2"><span class="label">Pearson ID:</span> ${pearsonId}</div>` : ''}

    <div class="mt-4 justify">
        This letter is issued at the request of <strong>${refLineName}</strong>.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAttendanceFirstWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>1st Attendance Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>1st Warning Letter Regarding Poor Attendance</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>1st warning</strong> regarding your <strong>poor attendance</strong>. Regular attendance is essential for your academic success and to meet the requirements set forth by our institution and the university. Our records indicate that your attendance has fallen below the acceptable threshold.
    </div>

    <div class="mt-2 justify">
        To address this issue, you are required to attend all upcoming classes consistently without any further absences. Additionally, you must provide valid justification for any prior absences, along with relevant supporting documents, if applicable.
    </div>

    <div class="mt-2 justify">
        Failure to comply with these requirements within <strong>14 days</strong> from the date of this letter will result in further actions, including:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        We urge you to take this warning seriously and make the necessary efforts to improve your attendance.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this warning letter and agree to take the necessary steps to improve my attendance as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAttendanceSecondWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>2nd Attendance Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>2nd Warning Letter Regarding Poor Attendance</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>2nd warning</strong> regarding your <strong>poor attendance</strong>. Despite our previous communication, your attendance continues to remain below the acceptable threshold required by the institution and the university.
    </div>

    <div class="mt-2 justify">
        You are hereby instructed to attend all upcoming classes without fail and to take immediate corrective action to improve your attendance. You are also required to provide valid justification, with supporting documents where applicable, for your previous absences.
    </div>

    <div class="mt-2 justify">
        Please be advised that failure to comply with these instructions may result in the following actions:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are strongly advised to treat this matter with seriousness and to ensure full compliance with the attendance policy.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this 2nd warning letter and agree to take the necessary steps to improve my attendance as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAttendanceThirdWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>3rd Attendance Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>3rd and Final Warning Letter Regarding Poor Attendance</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>3rd and final warning</strong> regarding your <strong>poor attendance</strong>. Our records show that, despite previous warnings, your attendance continues to fall below the required standard.
    </div>

    <div class="mt-2 justify">
        Please be informed that continued failure to improve your attendance will result in immediate administrative action as per the institution's regulations.
    </div>

    <div class="mt-2 justify">
        The consequences may include, but are not limited to, the following:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
            <li><strong>Cancellation of Registration</strong>: Your registration for the current programme may be cancelled, subject to institutional policy.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are strongly urged to take immediate steps to comply with the attendance requirements and to avoid any further disciplinary action.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this 3rd and final warning letter and understand the possible consequences of failing to improve my attendance as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAttendanceBatchTransferLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Batch Transfer Letter Due to Poor Attendance</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}${batchName ? ' - ' + batchName : ''}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>Notification of Batch Transfer Due to Poor Attendance</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter is to formally notify you that, following the issuance of <strong>three attendance warning letters</strong> and due to consistently <strong>poor attendance</strong> below the required threshold, you will be transferred to a subsequent batch.
    </div>

    <div class="mt-2 justify">
        As per the institution's policies, the following terms will be applied:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You will be transferred to the next available batch.</li>
            <li><strong>Transfer Fee</strong>: A batch transfer fee will be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch will be revoked, and you will no longer be eligible for such benefits.</li>
        </ol>
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this letter and agree to the batch transfer with the applicable transfer fee, extension fee, and the cancellation of any discounts provided for the current batch.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openSubmissionFirstWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>1st Submission Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>1st Warning Letter Regarding Pending Assignments</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>1st warning</strong> regarding your <strong>pending assignments</strong>. Timely submission of assignments is essential to maintain your academic progress and adhere to the standards of our institution.
    </div>

    <div class="mt-2 justify">
        You are required to submit all pending assignments within the next <strong>14 days</strong> from the date of this letter. Failure to comply with this deadline will result in the following actions:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        We emphasize the importance of taking this warning seriously to avoid any disruptions to your academic journey.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this warning letter and agree to submit the pending assignments within the next <strong>14 days</strong> as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openSubmissionSecondWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>2nd Submission Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>2nd Warning Letter Regarding Pending Assignments</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>2nd warning</strong> regarding your <strong>pending assignments</strong>. Despite our previous communication, you have not submitted all required assignments within the stipulated time.
    </div>

    <div class="mt-2 justify">
        You are hereby instructed to submit all pending assignments without further delay. Failure to comply may result in the following actions:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are strongly advised to address these pending submissions immediately to avoid disruptions to your academic progress.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this 2nd warning letter and agree to submit all pending assignments as instructed above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openSubmissionThirdWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>3rd Submission Warning Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>3rd and Final Warning Letter Regarding Pending Assignments</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>3rd and final warning</strong> regarding your <strong>pending assignments</strong>. Despite previous warnings, you have not completed and submitted all required assignments.
    </div>

    <div class="mt-2 justify">
        Please be informed that failure to submit the pending assignments immediately may result in the following actions being taken in line with institutional policy:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You may be transferred to a subsequent batch.</li>
            <li><strong>Transfer Fee</strong>: A transfer fee may be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch may be revoked.</li>
            <li><strong>Cancellation of Registration</strong>: Your registration for the current programme may be cancelled, subject to institutional policy.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are strongly urged to take immediate steps to complete and submit all pending assignments to avoid these consequences.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this 3rd and final warning letter and understand the possible consequences of failing to submit my pending assignments as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openSubmissionBatchTransferLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Batch Transfer Letter Due to Pending Assignments</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}${batchName ? ' - ' + batchName : ''}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>Notification of Batch Transfer Due to Pending Assignments</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter is to formally notify you that, following the issuance of <strong>three submission warning letters</strong> and due to continued <strong>pending assignments</strong> that have not been submitted within the required timeframes, you will be transferred to a subsequent batch.
    </div>

    <div class="mt-2 justify">
        As per the institution's policies, the following terms will be applied:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: You will be transferred to the next available batch.</li>
            <li><strong>Transfer Fee</strong>: A batch transfer fee will be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to a batch transfer, your registration expires before completion of the course, you will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for your current batch will be revoked, and you will no longer be eligible for such benefits.</li>
        </ol>
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this letter and agree to the batch transfer with the applicable transfer fee, extension fee, and the cancellation of any discounts provided for the current batch.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openCompletionLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const nic = student.nicNumber || 'N/A';
        const courseName = reg.courseName || 'Course';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';
        const pearsonId = reg.pearsonId || '';
        const universityId = student.universityId || student.universityID || '';
        const location = 'Batticaloa';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const gender = (student.gender || '').toString().toLowerCase();
        let titlePrefix = '';
        if (gender === 'male' || gender === 'm') titlePrefix = 'Mr. ';
        else if (gender === 'female' || gender === 'f') titlePrefix = 'Ms. ';

        const refLineName = `${titlePrefix}${name}`.trim();

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Completion Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <br /><br />
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4">
        <span class="label">Ref:</span> ${refLineName} (NIC No: ${nic})
    </div>

    <div class="mt-4 justify">
        This is to certify that <strong>${refLineName}</strong> has successfully completed the course titled <strong>${courseName}</strong> at <strong>ESOFT Metro Campus</strong> under the registration number <strong>${regNumber}</strong>.
    </div>

    <div class="mt-2"><span class="label">Studied Location:</span> ${location}</div>

    ${universityId ? `<div class="mt-2"><span class="label">University ID:</span> ${universityId}</div>` : ''}
    ${pearsonId ? `<div class="mt-2"><span class="label">Pearson ID:</span> ${pearsonId}</div>` : ''}

    <div class="mt-4 justify">
        The student has fulfilled all academic requirements and other obligations stipulated by the institution, and is therefore considered to have successfully completed the above course.
    </div>

    <div class="mt-4 justify">
        This letter is issued at the request of <strong>${refLineName}</strong> for official and record purposes.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openUniversityActivationRequestLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const nic = student.nicNumber || 'N/A';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Activation Request for Inactive Registration (Delayed Payment)</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .mt-8 { margin-top: 32px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4">
        <span class="label">From:</span><br />
        ${name}<br />
        Student ID: ${studentId}${batchName ? ` / ${batchName}` : ''}<br />
        NIC No: ${nic}
    </div>

    <div class="mt-4">
        <span class="label">To:</span><br />
        The Manager / Programme Coordinator<br />
        ESOFT Metro Campus - Batticaloa
    </div>

    <div class="mt-4"><span class="label">Subject:</span> Request to Activate Studies for Inactive Registration Due to Delayed Payment</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-2 justify">
        My name is <strong>${name}</strong> (Student ID: <strong>${studentId}</strong>). I was registered for <strong>${courseName}</strong>${batchName ? ` under the batch <strong>${batchName}</strong>` : ''}, and due to a delay in making my required payments my registration status has become <strong>inactive</strong>.
    </div>

    <div class="mt-2 justify">
        I kindly request that my registration and studies be <strong>reactivated</strong> in the current batch.
    </div>

    <div class="mt-2 justify">
        I understand that, as a result of this delayed payment, my <strong>university registration, graduation timeline and certificate claiming</strong> may also be delayed, and that my <strong>university/university partner registration number</strong> will be issued only after I complete all required procedures and payments.
    </div>

    <div class="mt-2 justify">
        I confirm that, once this request is approved, I will settle all outstanding <strong>course and university-related payments</strong> without delay, and I respectfully request your approval to continue my studies.
    </div>

    <div class="mt-4 justify">
        Thank you very much for your time and support.
    </div>

    <div class="mt-8 label underline">Student Acknowledgment and Agreement</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong> (Student ID: <strong>${studentId}</strong>), confirm that I understand the above conditions relating to reactivation of my studies after delayed payment and inactive registration status. I agree to comply with all academic and financial requirements and accept that any delay in <strong>graduation</strong>, <strong>certificate issuing/claiming</strong> and related processes arising from this delay will apply to me.
    </div>

    <div class="mt-4">Signature of Student: ________________________________</div>
    <div class="mt-2">Full Name of Student: ______________________________</div>
    <div class="mt-2">Date: _________________________________</div>

</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openBatchTransferRequestLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const nic = student.nicNumber || 'N/A';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Batch Transfer Request</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .mt-8 { margin-top: 32px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .underline { text-decoration: underline; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4">
        <span class="label">From:</span><br />
        ${name}<br />
        Student ID: ${studentId}${batchName ? ` / ${batchName}` : ''}<br />
        NIC No: ${nic}
    </div>

    <div class="mt-4">
        <span class="label">To:</span><br />
        The Manager / Programme Coordinator<br />
        ESOFT Metro Campus - Batticaloa
    </div>

    <div class="mt-4"><span class="label">Subject:</span> Request for Batch Transfer</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-2 justify">
        I, <strong>${name}</strong> (Student ID: <strong>${studentId}</strong>), am currently registered for <strong>${courseName}</strong>${batchName ? ` under the batch <strong>${batchName}</strong>` : ''}. I hereby kindly request a <strong>transfer to the next available batch</strong> of this programme.
    </div>

    <div class="mt-2 justify">
        I understand and agree to the following conditions relating to this batch transfer:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Batch Transfer</strong>: I will be transferred to the next available batch as decided by the institution.</li>
            <li><strong>Transfer Fee</strong>: A batch transfer fee will be applicable.</li>
            <li><strong>Extension Fee</strong>: If, due to this batch transfer, my registration expires before completion of the course, I will be required to pay the applicable extension fee.</li>
            <li><strong>Offer Cancellation</strong>: Any offers or discounts provided for my current batch will be revoked, and I will no longer be eligible for such benefits.</li>
        </ol>
    </div>

    <div class="mt-4 label underline">Reason for Batch Transfer (to be completed by student)</div>
    <div class="mt-2">__________________________________________________________________________________</div>
    <div class="mt-2">__________________________________________________________________________________</div>
    <div class="mt-2">__________________________________________________________________________________</div>

    <div class="mt-4 justify">
        I respectfully request your kind consideration and approval of this batch transfer request.
    </div>

    <div class="mt-4">Thank you.</div>

    <div class="mt-6">Signature of Student: ________________________________</div>
    <div class="mt-2">Full Name of Student: ______________________________</div>
    <div class="mt-2">Date: _________________________________</div>

</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAcademicAcceptableLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Academic Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Academic Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        This is to recommend <strong>${name}</strong>, who successfully completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa.
    </div>

    <div class="mt-2 justify">
        During the course of study, ${name} demonstrated acceptable performance in academic work. The student met the basic requirements and standards expected for the programme and completed all necessary coursework within the stipulated timeframe.
    </div>

    <div class="mt-2 justify">
        ${name} showed adequate understanding of the subject matter and fulfilled all academic obligations satisfactorily. The student maintained acceptable attendance and participation throughout the duration of the course.
    </div>

    <div class="mt-2 justify">
        We believe ${name} has acquired the foundational knowledge and skills necessary for further academic pursuits in this field.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>

    

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAcademicAverageLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Academic Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Academic Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        I am pleased to recommend <strong>${name}</strong>, who successfully completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa.
    </div>

    <div class="mt-2 justify">
        Throughout the course of study, ${name} demonstrated good academic performance and a solid understanding of the subject matter. The student consistently achieved above-average results and showed genuine interest in the coursework.
    </div>

    <div class="mt-2 justify">
        ${name} displayed commendable work ethic, maintained regular attendance, and actively participated in class discussions and group activities. The student effectively balanced theoretical knowledge with practical application.
    </div>

    <div class="mt-2 justify">
        We are confident that ${name} possesses the academic capabilities and dedication required for further studies in this field and will prove to be a valuable asset to any academic institution.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>


    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openAcademicBestLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Academic Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Academic Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        It is with great pleasure that I recommend <strong>${name}</strong>, who completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa with outstanding academic distinction.
    </div>

    <div class="mt-2 justify">
        Throughout the course of study, ${name} consistently demonstrated exceptional academic performance, achieving top grades and excelling in all aspects of the programme. The student displayed remarkable analytical skills, critical thinking abilities, and a deep understanding of complex subject matter.
    </div>

    <div class="mt-2 justify">
        ${name} was an exemplary student who not only excelled academically but also actively contributed to the learning environment through insightful questions and collaborative projects. The student's dedication, perseverance, and intellectual curiosity set them apart from their peers.
    </div>

    <div class="mt-2 justify">
        We have no hesitation in strongly recommending ${name} for any advanced academic programme. The student possesses the intellectual capability, motivation, and work ethic necessary to excel at the highest levels of academic pursuit.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>


    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openJobAcceptableLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Job Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Job Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        This is to recommend <strong>${name}</strong>, who successfully completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa.
    </div>

    <div class="mt-2 justify">
        During the course of study, ${name} demonstrated acceptable performance and acquired the foundational skills and knowledge relevant to the field. The student completed all required coursework and practical assignments satisfactorily.
    </div>

    <div class="mt-2 justify">
        ${name} showed adequate understanding of core concepts and fulfilled all academic and practical obligations required by the programme. The student maintained regular attendance and participated appropriately in all activities.
    </div>

    <div class="mt-2 justify">
        We believe ${name} is capable of performing entry-level responsibilities in the field and has the basic competencies required for employment in related positions.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>


    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openJobAverageLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Job Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Job Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        I am pleased to recommend <strong>${name}</strong>, who successfully completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa.
    </div>

    <div class="mt-2 justify">
        Throughout the programme, ${name} demonstrated good performance and acquired solid practical and theoretical knowledge in the field. The student showed commendable work ethic and a genuine interest in developing professional skills.
    </div>

    <div class="mt-2 justify">
        ${name} consistently performed well in both individual and team-based assignments, displaying good problem-solving abilities and effective communication skills. The student maintained regular attendance and actively participated in practical sessions and projects.
    </div>

    <div class="mt-2 justify">
        We are confident that ${name} possesses the competencies and dedication required to perform well in professional settings and will be a reliable and productive employee.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>


    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openJobBestLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const courseName = reg.courseName || '';
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Job Recommendation Letter</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .signature { margin-top: 60px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><strong>TO WHOM IT MAY CONCERN</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Job Recommendation Letter</div>

    <div class="mt-4">Dear Sir/Madam,</div>

    <div class="mt-4 justify">
        It is with great pleasure that I recommend <strong>${name}</strong>, who completed <strong>${courseName}</strong> at ESOFT Metro Campus Batticaloa with outstanding achievement.
    </div>

    <div class="mt-2 justify">
        Throughout the programme, ${name} consistently demonstrated exceptional performance, technical proficiency, and professional excellence. The student excelled in both theoretical understanding and practical application, achieving top results in all assessments and projects.
    </div>

    <div class="mt-2 justify">
        ${name} exhibited remarkable problem-solving abilities, strong leadership skills, and excellent team collaboration. The student took initiative in complex projects, demonstrated creativity in finding solutions, and consistently delivered high-quality work that exceeded expectations.
    </div>

    <div class="mt-2 justify">
        We have no hesitation in strongly recommending ${name} for any professional position in the field. The student possesses exceptional technical skills, professional maturity, and the drive to excel in challenging work environments. We are confident that ${name} will be an outstanding asset to any organization.
    </div>

    <div class="mt-4 justify">
        Should you require any further information, please do not hesitate to contact us.
    </div>

    <div class="mt-6">Thank you.</div>


    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openRegisteredPostInactivationLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';
        const campusName = 'ESOFT Metro Campus Batticaloa';

        const gender = (student.gender || '').toString().toLowerCase();
        let titlePrefix = '';
        if (gender === 'male' || gender === 'm') titlePrefix = 'Mr. ';
        else if (gender === 'female' || gender === 'f') titlePrefix = 'Ms. ';
        const salutationName = `${titlePrefix}${name}`.trim() || name;

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Registered Post – Inactivation Notice</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .signature { margin-top: 60px; }
        ul { margin-left: 20px; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${salutationName || name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Registered Post – Notice to Reactivate Inactivated Registration</div>

    <div class="mt-4">Dear <strong>${salutationName || name}</strong>,</div>

    <div class="mt-4 justify">
        This registered post is issued to formally notify you that your registration for <strong>${courseName}</strong> (Registration No: <strong>${regNumber}</strong>) has been <strong>inactivated</strong> in accordance with ESOFT Metro Campus regulations. Essential requirements remain outstanding despite previous reminders issued by the campus.
    </div>

    <div class="mt-2 justify">
        The outstanding matters include one or more of the following:
        <ul>
            <li>Overdue tuition or university-related payments</li>
            <li>Missing mandatory documentation or acknowledgements</li>
            <li>Lack of response to earlier written and verbal notices</li>
        </ul>
    </div>

    <div class="mt-2 justify">
        You are required to visit the campus within <strong>10 working days</strong> from the date of this letter, meet the course coordinator or HOD, and complete the necessary actions to reactivate your registration in alignment with ESOFT Metro Campus rules and regulations.
    </div>

    <div class="mt-2 justify">
        Until the reactivation process is completed, the following limitations will apply:
        <ul>
            <li>Loss of access to classes, LMS content, assignment submissions, and examination scheduling</li>
            <li>Suspension of certificate requests, transcript processing, and graduation endorsements</li>
            <li>Requirement to pay reactivation or extension fees to resume studies</li>
        </ul>
    </div>

    <div class="mt-2 justify">
        Please treat this letter with urgency and contact the campus immediately if you require clarification or support to regularize your status.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>${campusName}</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openRegisteredPostAttendanceLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || reg.batch || '';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Registered Post – Attendance Notice</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .signature { margin-top: 60px; }
        ul { margin-left: 20px; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Registered Post – Attendance Deficiency Notice</div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This registered post serves as a formal notification that your attendance for <strong>${courseName}</strong> (Registration No: <strong>${regNumber}</strong>) remains below the minimum standard required by the campus and the awarding body. Previous reminders have not resulted in a satisfactory improvement.
    </div>

    <div class="mt-2 justify">
        You are required to attend all remaining sessions, practicals, and revision meetings, and to meet with the course coordinator within <strong>7 working days</strong> from the date of this letter to confirm your recovery plan. Supporting documents for any unavoidable absences must also be submitted.
    </div>

    <div class="mt-2 justify">
        Please be advised that continued non-compliance will trigger the following actions:
        <ul>
            <li>Immediate hold on continuous assessment endorsements and attendance certificates</li>
            <li>Possible postponement of examination entries or graduation timelines</li>
            <li>Mandatory payment of extension fees to complete the programme requirements</li>
        </ul>
    </div>

    <div class="mt-2 justify">
        Treat this communication as urgent. Contact the course coordinator or HOD if you need assistance to regularize your attendance.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openRegisteredPostAcademicLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const focusLabel = assignmentNoticeType === 'exam-attendance'
            ? 'poor exam attendance'
            : 'pending assignment submissions';
        const actionInstruction = assignmentNoticeType === 'exam-attendance'
            ? 'attend the scheduled examinations, preparatory workshops, and compulsory briefing sessions'
            : 'complete and submit the remaining assignments with the necessary lecturer acknowledgements';
        const documentationInstruction = assignmentNoticeType === 'exam-attendance'
            ? 'Any official documents that justify missed examinations must be submitted together with your rescheduling request.'
            : 'Please attach any drafts or supervisor feedback when handing over the completed assignments.';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Registered Post – Academic Progress Notice</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .signature { margin-top: 60px; }
        ul { margin-left: 20px; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Registered Post – Academic Progress Notice</div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This registered post is sent to formally notify you of <strong>${focusLabel}</strong> relating to your registration (Reg. No: <strong>${regNumber}</strong>). Immediate corrective action is required to safeguard your academic progress.
    </div>

    <div class="mt-2 justify">
        You are instructed to ${actionInstruction} within <strong>7 working days</strong> from the date of this letter. ${documentationInstruction}
    </div>

    <div class="mt-2 justify">
        Please note that failure to comply within the stipulated timeframe will lead to the following outcomes:
        <ul>
            <li>Delays in certificate requests, transcript processing, and graduation approval</li>
            <li>Ineligibility to claim awards or participate in scheduled convocation cycles</li>
            <li>Liability to pay extension or reactivation fees to complete the programme</li>
        </ul>
    </div>

    <div class="mt-2 justify">
        Treat this notice with urgency and liaise with your lecturer or the course coordinator/HOD for any support required to complete the outstanding academic tasks.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openRegisteredPostParentMeetingLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        if (!parentMeetingDate) {
            Swal.fire('Meeting date required', 'Please select a parents meeting date before printing the letter.', 'info');
            return;
        }

        const meetingDateObj = new Date(parentMeetingDate);
        if (Number.isNaN(meetingDateObj.getTime())) {
            Swal.fire('Invalid date', 'Selected meeting date is not valid. Please choose another date.', 'error');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const meetingDateLabel = meetingDateObj.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Registered Post – Parents Meeting Invitation</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .signature { margin-top: 60px; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> Parent / Guardian of <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Registered Post – Invitation for Parents' Meeting</div>

    <div class="mt-4">Dear Parent / Guardian,</div>

    <div class="mt-4 justify">
        This registered post is to formally invite you to a parents' meeting regarding the academic progress and student support plan of <strong>${name}</strong> (Registration No: <strong>${regNumber}</strong>). Your presence is important to review the current status and to agree on the next steps required for successful course completion.
    </div>

    <div class="mt-2 justify">
        <span class="label">Meeting Date:</span> ${meetingDateLabel}<br />
        <span class="label">Venue:</span> ESOFT Metro Campus Batticaloa – Administration Office<br />
        <span class="label">Purpose:</span> Academic review, attendance feedback, and support planning
    </div>

    <div class="mt-2 justify">
        Please confirm your availability with the course coordinator or HOD at least <strong>3 working days</strong> before the meeting date. If you are unable to attend in person, kindly nominate an authorized representative or contact us to arrange an alternate discussion slot.
    </div>

    <div class="mt-2 justify">
        We appreciate your cooperation and look forward to meeting you to ensure that ${name} receives the necessary guidance to remain in good academic standing.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openHndPathwayVerificationLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const courseName = reg.courseName || '';
        const lowerName = courseName.toLowerCase();
        if (!(lowerName.includes('hnd') && lowerName.includes('comput'))) {
            Swal.fire('Not applicable', 'This letter applies only to HND in Computing registrations.', 'info');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const regNumber = formatRegistrationNumberBase(reg.registrationNumber) || 'N/A';

        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Pathway Verification Deadline Lapsed</title>
    <style>
        body { font-family: 'Times New Roman', Times, serif; margin: 40px; font-size: 12pt; line-height: 1.5; }
        .mt-2 { margin-top: 8px; }
        .mt-4 { margin-top: 16px; }
        .mt-6 { margin-top: 24px; }
        .label { font-weight: 600; }
        .justify { text-align: justify; }
        .signature { margin-top: 60px; }
        ul { margin-left: 20px; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> Pathway Verification Deadline Lapsed</div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter records that the pathway verification deadline for your HND in Computing registration (Reg. No: <strong>${regNumber}</strong>) has passed, even though multiple reminders were shared through the official batch WhatsApp group and in-class announcements.
    </div>

    <div class="mt-2 justify">
        You are required to visit the campus within <strong>3 working days</strong> from the date of this letter, meet the course coordinator or HOD, and confirm your academic pathway as either <strong>Software Engineering</strong> or <strong>Network Engineering</strong>. Please provide the signed pathway verification form at this meeting so that the campus can align your assessments, university registration, and progression plans accordingly.
    </div>

    <div class="mt-2 justify">
        Delays in pathway confirmation will have the following consequences:
        <ul>
            <li>Certificate requests, transcript processing, and graduation approvals will be postponed.</li>
            <li>You may become ineligible for scheduled university submissions or exam boards.</li>
            <li>If your registration expires before completion, extension fees will apply to continue the course.</li>
        </ul>
    </div>

    <div class="mt-2 justify">
        Treat this notice as urgent and finalize your pathway verification without any further delay.
    </div>

    <div class="mt-6">Thank you.</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print on new window:', e);
            }
        }, 300);
    };

    const openBehaviorFirstWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || '';

        const currentUser = AuthService.getUser();
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>First Behavioral Warning Letter</title>
    <style>
        body { 
            font-family: 'Times New Roman', Times, serif; 
            font-size: 12pt; 
            line-height: 1.5; 
            margin: 40px;
        }
        .mt-2 { margin-top: 0.5rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-6 { margin-top: 1.5rem; }
        .signature { margin-top: 2rem; }
        .label { font-weight: normal; }
        .underline { text-decoration: underline; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>First Warning Letter Regarding Behavioral Misconduct</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as a <strong>first warning</strong> regarding your recent <strong>behavioral misconduct</strong> at ESOFT Metro Campus Batticaloa. It has come to our attention that your conduct does not align with the standards of behavior expected from our students.
    </div>

    <div class="mt-2 justify">
        As a student of this institution, you are expected to maintain proper conduct, respect for staff and peers, and adherence to institutional policies at all times. Your recent actions have been noted, and such behavior is unacceptable within our academic community.
    </div>

    <div class="mt-2 justify">
        Please consider this letter as a <strong>first formal warning</strong>. You are required to improve your behavior immediately. Failure to do so may result in further disciplinary actions, including but not limited to:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Second Warning</strong>: A more severe written warning will be issued.</li>
            <li><strong>Suspension</strong>: Temporary suspension from classes and campus activities.</li>
            <li><strong>Final Warning</strong>: A final warning before possible dismissal.</li>
            <li><strong>Dismissal</strong>: Permanent removal from the institution and cancellation of your registration.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        We expect you to reflect on this matter seriously and make the necessary changes to your conduct. You are encouraged to meet with the course coordinator or student welfare officer if you need guidance or support.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this first warning letter regarding behavioral misconduct and understand the consequences of failing to improve my conduct as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

  

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) return;
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print:', e);
            }
        }, 300);
    };

    const openBehaviorSecondWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || '';

        const currentUser = AuthService.getUser();
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Second Behavioral Warning Letter</title>
    <style>
        body { 
            font-family: 'Times New Roman', Times, serif; 
            font-size: 12pt; 
            line-height: 1.5; 
            margin: 40px;
        }
        .mt-2 { margin-top: 0.5rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-6 { margin-top: 1.5rem; }
        .signature { margin-top: 2rem; }
        .label { font-weight: normal; }
        .underline { text-decoration: underline; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>Second Warning Letter Regarding Behavioral Misconduct</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as a <strong>second warning</strong> regarding your continued <strong>behavioral misconduct</strong>. Despite receiving a first warning, your conduct has not shown adequate improvement and continues to fall short of the standards expected at ESOFT Metro Campus Batticaloa.
    </div>

    <div class="mt-2 justify">
        We take this matter very seriously. Your repeated failure to adhere to proper conduct, respect institutional policies, and maintain acceptable behavior is deeply concerning. Such conduct disrupts the academic environment and is detrimental to the learning community.
    </div>

    <div class="mt-2 justify">
        This is your <strong>second and penultimate warning</strong>. Immediate and sustained improvement in your behavior is mandatory. Failure to demonstrate significant behavioral change may result in the following escalated actions:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Suspension</strong>: You may be suspended from attending classes and campus activities for a specified period.</li>
            <li><strong>Final Warning</strong>: A final warning will be issued with the possibility of immediate dismissal.</li>
            <li><strong>Academic Probation</strong>: You may be placed on academic probation affecting your academic standing.</li>
            <li><strong>Dismissal</strong>: Permanent dismissal from the institution and cancellation of your registration.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are strongly advised to take this warning seriously and seek guidance from the course coordinator or student welfare officer immediately. This is your opportunity to rectify your conduct before facing more severe consequences.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this second warning letter regarding behavioral misconduct and understand the serious consequences of failing to improve my conduct as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) return;
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print:', e);
            }
        }, 300);
    };

    const openBehaviorFinalWarningLetterTab = () => {
        if (!selectedRegistrationId) {
            Swal.fire('Select a course', 'Please select a registered course first.', 'info');
            return;
        }

        const reg = registrations.find((r) => r.registrationId === selectedRegistrationId);
        if (!reg) {
            Swal.fire('Course not found', 'Selected registration could not be found.', 'error');
            return;
        }

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = today.toLocaleString('en-GB', { month: 'long' });
        const year = today.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        const name = student.fullName || '';
        const studentId = student.studentId || '';
        const courseName = reg.courseName || '';
        const batchName = reg.batchName || '';

        const currentUser = AuthService.getUser();
        const userName = currentUser?.superAdmin?.staff?.name || '';
        const userTitle = currentUser?.superAdmin?.staff?.title || currentUser?.title || '';
        const userPosition = currentUser?.superAdmin?.staff?.positionName || currentUser?.superAdmin?.staff?.designation || '';

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Final Behavioral Warning Letter</title>
    <style>
        body { 
            font-family: 'Times New Roman', Times, serif; 
            font-size: 12pt; 
            line-height: 1.5; 
            margin: 40px;
        }
        .mt-2 { margin-top: 0.5rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-6 { margin-top: 1.5rem; }
        .signature { margin-top: 2rem; }
        .label { font-weight: normal; }
        .underline { text-decoration: underline; }
        .justify { text-align: justify; }
        @media print {
            @page { margin: 0; }
            body { margin: 1.6cm; }
        }
    </style>
</head>
<body>
    <div><span class="label">Date:</span> ${formattedDate}</div>

    <div class="mt-4"><span class="label">To:</span> <strong>${name}</strong></div>
    <div class="mt-2"><span class="label">Student ID:</span> <strong>${studentId}</strong></div>
    ${batchName ? `<div class="mt-2"><span class="label">Batch:</span> <strong>${batchName}</strong></div>` : ''}
    <div class="mt-2"><span class="label">Course:</span> <strong>${courseName}</strong></div>

    <div class="mt-4"><span class="label">Subject:</span> <strong>Final Warning Letter Regarding Behavioral Misconduct</strong></div>

    <div class="mt-4">Dear <strong>${name}</strong>,</div>

    <div class="mt-4 justify">
        This letter serves as your <strong>final and last warning</strong> regarding your persistent and serious <strong>behavioral misconduct</strong>. Despite having received two previous warnings, you have failed to demonstrate any meaningful improvement in your conduct, attitude, or adherence to institutional policies.
    </div>

    <div class="mt-2 justify">
        Your continued unacceptable behavior poses a significant disruption to the academic environment, violates the code of conduct, and undermines the values and reputation of ESOFT Metro Campus Batticaloa. This institution will not tolerate such conduct any further.
    </div>

    <div class="mt-2 justify">
        This is your <strong>final warning</strong>. Any further instances of behavioral misconduct will result in <strong>immediate dismissal</strong> from the institution. Additionally, failure to show immediate and sustained improvement may lead to:
    </div>

    <div class="mt-2 justify">
        <ol style="margin-left: 20px;">
            <li><strong>Immediate Suspension</strong>: You may be suspended from all classes and campus activities pending disciplinary review.</li>
            <li><strong>Dismissal from Institution</strong>: Permanent expulsion from ESOFT Metro Campus and termination of your student status.</li>
            <li><strong>Cancellation of Registration</strong>: Your course registration will be permanently cancelled, and you will not be permitted to re-register.</li>
            <li><strong>Forfeiture of Fees</strong>: All fees paid may be forfeited as per institutional policy, with no refund provided.</li>
            <li><strong>Notation on Academic Records</strong>: Dismissal for misconduct may be noted on your academic transcript.</li>
        </ol>
    </div>

    <div class="mt-2 justify">
        You are <strong>strongly urged</strong> to immediately correct your behavior and conduct yourself in a manner befitting a student of this institution. This is your last opportunity to avoid dismissal. Failure to comply will result in immediate action.
    </div>

    <div class="mt-6 label underline">Student Acknowledgment</div>
    <div class="mt-2 justify">
        I, <strong>${name}</strong>, acknowledge receipt of this final warning letter regarding behavioral misconduct and fully understand that any further misconduct will result in my immediate dismissal from the institution, as outlined above.
    </div>

    <div class="mt-4">Signature: ___________________________</div>
    <div class="mt-2">Date: _______________________________</div>

    <div class="signature">
        <div>Yours faithfully,</div>
        ${userName ? `<div>${userTitle ? `<span>${userTitle}. </span>` : ''}${userName}</div>` : ''}
        ${userPosition ? `<div>${userPosition}</div>` : ''}
        <div>ESOFT Metro Campus Batticaloa</div>
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) return;
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) {
                console.error('Failed to trigger print:', e);
            }
        }, 300);
    };

    const renderTabContent = () => {
    switch (activeTab) {
      case 'letters':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">General Letters</h3>
            <p className="text-sm text-slate-600">Generate and print general letters for the student.</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                                        <button 
                                            onClick={() => { setSelectedLetter('following'); openFollowingLetterTab(); }}
                                            className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left hover:bg-slate-50 hover:border-slate-400 transition-all"
                                        >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-slate-100 p-2">
                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">Following Letter</p>
                                        <p className="text-xs text-slate-500">General follow-up communication</p>
                                    </div>
                </div>
                            </button>
                            <button 
																	onClick={() => { setSelectedLetter('completion'); openCompletionLetterTab(); }}
                                className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left hover:bg-slate-50 hover:border-slate-400 transition-all"
                            >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-slate-100 p-2">
                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">Completion Letter</p>
                                        <p className="text-xs text-slate-500">Course completion confirmation</p>
                                    </div>
                </div>
              </button>
                            <button 
                                onClick={() => { setSelectedLetter('university-activation-request'); openUniversityActivationRequestLetterTab(); }}
                                className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left hover:bg-slate-50 hover:border-slate-400 transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="rounded-lg bg-slate-100 p-2">
                                        <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M5 13a7 7 0 1114 0 7 7 0 01-14 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">University Activation Request</p>
                                        <p className="text-xs text-slate-500">When university registration is pending</p>
                                    </div>
                                </div>
                            </button>
                            <button 
                                onClick={() => { setSelectedLetter('batch-transfer-request'); openBatchTransferRequestLetterTab(); }}
                                className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left hover:bg-slate-50 hover:border-slate-400 transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="rounded-lg bg-slate-100 p-2">
                                        <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">Batch Transfer Request</p>
                                        <p className="text-xs text-slate-500">Student-initiated batch transfer</p>
                                    </div>
                                </div>
                            </button>
            </div>
          </div>
        );
      case 'warning':
        return (
                    <div className="space-y-6">

                        {/* Group 1: Attendance Warning Letter */}
                        <div className="space-y-3">
                            <p className="text-sm font-semibold text-amber-800">Attendance Warning Letter</p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <button
								  onClick={() => { setSelectedLetter('attendance-first-warning'); openAttendanceFirstWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">First Warning Letter</p>
                                            <p className="text-xs text-amber-600">Initial attendance warning</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                								  onClick={() => { setSelectedLetter('attendance-second-warning'); openAttendanceSecondWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Second Warning Letter</p>
                                            <p className="text-xs text-amber-600">Repeated attendance concern</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                								  onClick={() => { setSelectedLetter('attendance-third-warning'); openAttendanceThirdWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Third Warning Letter</p>
                                            <p className="text-xs text-amber-600">Final attendance warning</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('attendance-batch-transfer'); openAttendanceBatchTransferLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Batch Transfer Letter</p>
                                            <p className="text-xs text-amber-600">Attendance related transfer</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Group 2: Submission Warning Letter */}
                        <div className="space-y-3">
                            <p className="text-sm font-semibold text-amber-800">Submission Warning Letter</p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <button
                                    onClick={() => { setSelectedLetter('submission-first-warning'); openSubmissionFirstWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">First Warning Letter</p>
                                            <p className="text-xs text-amber-600">Initial submission warning</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('submission-second-warning'); openSubmissionSecondWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Second Warning Letter</p>
                                            <p className="text-xs text-amber-600">Repeated submission concern</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('submission-third-warning'); openSubmissionThirdWarningLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Third Warning Letter</p>
                                            <p className="text-xs text-amber-600">Final submission warning</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('submission-batch-transfer'); openSubmissionBatchTransferLetterTab(); }}
                                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-left hover:bg-amber-100 hover:border-amber-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-amber-100 p-2">
                                            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-800">Batch Transfer Letter</p>
                                            <p className="text-xs text-amber-600">Submission related transfer</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
        );
      case 'recommendation':
        return (
          <div className="space-y-6">

                        {/* Group 1: Academic Recommendation */}
                        <div className="space-y-3">
                            <p className="text-sm font-semibold text-green-800">Academic Recommendation</p>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <button
                                    onClick={() => { setSelectedLetter('academic-acceptable'); openAcademicAcceptableLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Acceptable Performance</p>
                                            <p className="text-xs text-green-600">Basic academic standards met</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('academic-average'); openAcademicAverageLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Average Performance</p>
                                            <p className="text-xs text-green-600">Good academic achievement</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('academic-best'); openAcademicBestLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Best Performance</p>
                                            <p className="text-xs text-green-600">Outstanding academic excellence</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Group 2: Job Recommendation */}
                        <div className="space-y-3">
                            <p className="text-sm font-semibold text-green-800">Job Recommendation</p>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <button
                                    onClick={() => { setSelectedLetter('job-acceptable'); openJobAcceptableLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Acceptable Performance</p>
                                            <p className="text-xs text-green-600">Meets job requirements</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('job-average'); openJobAverageLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Average Performance</p>
                                            <p className="text-xs text-green-600">Good work capability</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setSelectedLetter('job-best'); openJobBestLetterTab(); }}
                                    className="rounded-lg border-2 border-green-300 bg-green-50 p-4 text-left hover:bg-green-100 hover:border-green-400 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-green-100 p-2">
                                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-800">Best Performance</p>
                                            <p className="text-xs text-green-600">Exceptional work excellence</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
        );
            case 'registered-post':
                return (
                    <div className="space-y-6">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <h3 className="text-lg font-semibold text-slate-900">Registered Post Letters</h3>
                            <p className="text-sm text-slate-600">Produce formal registered post notices for critical student communications.</p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={() => { setSelectedLetter('registered-inactivation'); openRegisteredPostInactivationLetterTab(); }}
                                className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left transition-all hover:border-slate-500 hover:bg-slate-50"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="rounded-lg bg-slate-100 p-2">
                                        <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M5 7h14M5 7l7 5 7-5m-7 13h7a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2h4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">Inactivating Letter</p>
                                        <p className="text-xs text-slate-600">Notify the student that registration will be inactivated.</p>
                                    </div>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => { setSelectedLetter('registered-attendance'); openRegisteredPostAttendanceLetterTab(); }}
                                className="rounded-lg border-2 border-slate-300 bg-white p-4 text-left transition-all hover:border-slate-500 hover:bg-slate-50"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="rounded-lg bg-slate-100 p-2">
                                        <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">Poor Attendance Notification</p>
                                        <p className="text-xs text-slate-600">Formal registered post about insufficient attendance.</p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="font-semibold text-slate-900">Poor Assignment Submission</p>
                                        <p className="text-xs text-slate-600">Choose the focus and generate a registered post notice.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedLetter('registered-assignment'); openRegisteredPostAcademicLetterTab(); }}
                                        className="rounded-lg border border-slate-600 px-3 py-1 text-xs font-semibold text-slate-50 bg-slate-700 hover:bg-slate-800"
                                    >
                                        Print Letter
                                    </button>
                                </div>
                                <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-600">Letter Focus</label>
                                <select
                                    value={assignmentNoticeType}
                                    onChange={(e) => setAssignmentNoticeType(e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                >
                                    <option value="assignment-submission">Assignment submission delay</option>
                                    <option value="exam-attendance">Poor exam attendance</option>
                                </select>
                                <p className="mt-2 text-xs text-slate-500">Reminds the student that missing deadlines affects certificates, graduation, and may add extension fees.</p>
                            </div>

                            <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="font-semibold text-slate-900">Invitation for Parents Meeting</p>
                                        <p className="text-xs text-slate-600">Send a registered post invitation with a confirmed date.</p>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={!parentMeetingDate}
                                        onClick={() => { setSelectedLetter('registered-parent-meeting'); openRegisteredPostParentMeetingLetterTab(); }}
                                        className={`rounded-lg border px-3 py-1 text-xs font-semibold ${parentMeetingDate ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                                    >
                                        Print Letter
                                    </button>
                                </div>
                                <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-600">Meeting Date</label>
                                <input
                                    type="date"
                                    value={parentMeetingDate}
                                    onChange={(e) => setParentMeetingDate(e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                                />
                                <p className="mt-2 text-xs text-slate-500">A meeting date is required before printing the invitation.</p>
                            </div>
                        </div>

                        {showHndPathwayLetter && (
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <p className="font-semibold text-indigo-900">Pathway Verification Date Passed</p>
                                        <p className="text-xs text-indigo-700">
                                            Remind the student to confirm the Software Engineering or Network Engineering pathway immediately.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedLetter('hnd-pathway'); openHndPathwayVerificationLetterTab(); }}
                                        className="rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-indigo-700"
                                    >
                                        Print Pathway Letter
                                    </button>
                                </div>
                                <p className="mt-3 text-xs text-indigo-800">
                                    The pathway verification deadline is overdue despite repeated notifications on the official WhatsApp group and classroom board. Further delay affects certificate requests, graduation timelines, and may incur extension fees if the registration expires before completion.
                                </p>
                            </div>
                        )}
                    </div>
                );
      case 'behavior':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-red-800">Behavioral Warning Letters</h3>
            <p className="text-sm text-slate-600">Issue formal warnings for behavioral misconduct.</p>
                        <div className="grid gap-3 sm:grid-cols-3">
                            <button 
                                onClick={() => { setSelectedLetter('behavior-first'); openBehaviorFirstWarningLetterTab(); }}
                                className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-left hover:bg-red-100 hover:border-red-400 transition-all"
                            >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-100 p-2">
                    <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">First Warning</p>
                    <p className="text-xs text-red-600">Initial behavioral warning</p>
                  </div>
                </div>
                            </button>
                            <button 
                                onClick={() => { setSelectedLetter('behavior-second'); openBehaviorSecondWarningLetterTab(); }}
                                className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-left hover:bg-red-100 hover:border-red-400 transition-all"
                            >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-100 p-2">
                    <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">Second Warning</p>
                    <p className="text-xs text-red-600">Escalated warning</p>
                  </div>
                </div>
                            </button>
                            <button 
                                onClick={() => { setSelectedLetter('behavior-final'); openBehaviorFinalWarningLetterTab(); }}
                                className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-left hover:bg-red-100 hover:border-red-400 transition-all"
                            >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-100 p-2">
                    <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">Final Warning</p>
                    <p className="text-xs text-red-600">Last warning before dismissal</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        );
            default:
                return null;
        }
    };

      const renderLetterPreview = () => {
            if (!selectedLetter || !selectedRegistrationId) return null;

            const today = new Date().toLocaleDateString();
            const name = student.fullName;
            const id = student.studentId;
            const email = student.email || 'N/A';
            const selectedRegistration = registrations.find(
              (reg) => reg.registrationId === selectedRegistrationId
            );
            const courseName = selectedRegistration?.courseName || 'Course';
            const regNumber = selectedRegistration?.registrationNumber || 'N/A';
            const assignmentFocusLabel = assignmentNoticeType === 'exam-attendance'
                ? 'Poor exam attendance'
                : 'Pending assignment submissions';
            const parentMeetingDateLabel = parentMeetingDate
                ? new Date(parentMeetingDate).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                })
                : 'To be scheduled';

            const paragraphs = (() => {
                switch (selectedLetter) {
                case 'following':
                    return [
                            `This is to kindly follow up regarding the academic progress and general conduct of ${name} (Student ID: ${id}), who is/was registered for ${courseName} under registration number ${regNumber}.`,
                        'The purpose of this letter is to keep all concerned parties informed about the current status of the student and to encourage continuous communication with the institution.',
                        'If any further information or clarification is required, please do not hesitate to contact us.'
                    ];
                case 'completion':
                    return [
                            `This is to certify that ${name} (Student ID: ${id}) has successfully completed the course ${courseName} at our institution under registration number ${regNumber}.`,
                            'The student has fulfilled the academic requirements and other obligations stipulated by the institution, and is therefore eligible to be considered as having completed the above course.',
                        'This letter is issued upon request of the student for official and record purposes.'
                    ];
                case 'university-activation-request':
                    return [
                        `This letter is prepared on behalf of ${name} (Student ID: ${id}) to request activation of studies where the registration has become inactive due to delayed payment for ${courseName} (Reg. No: ${regNumber}).`,
                        'The student understands that, because of this delay, university registration, graduation timeline and certificate claiming may be affected.',
                        'The student confirms that all outstanding course and university-related payments will be settled without delay and accepts that any delay in graduation or certificate issuing arises from the late payment.'
                    ];
                case 'batch-transfer-request':
                    return [
                        `This letter is prepared on behalf of ${name} (Student ID: ${id}) to request a batch transfer for ${courseName} (Reg. No: ${regNumber}).`,
                        'The student understands that the transfer will be to the next available batch and that a transfer fee, any required extension fee, and the cancellation of discounts for the current batch may apply.',
                        'The student will provide a written reason for the transfer and confirms agreement with all batch transfer conditions set by the institution.'
                    ];
                case 'attendance-first-warning':
                    return [
                        `It has been observed that the attendance of ${name} (Student ID: ${id}) in ${courseName} (Reg. No: ${regNumber}) has fallen below the minimum requirement prescribed by the institution.`,
                        'This letter serves as the first formal reminder to maintain the required level of attendance in order to continue in the programme without interruption.',
                        'You are kindly advised to attend all future sessions regularly and to contact the course coordinator if any support is required.'
                    ];
                case 'attendance-second-warning':
                    return [
                        `Despite a previous warning, the attendance of ${name} (Student ID: ${id}) in ${courseName} (Reg. No: ${regNumber}) continues to remain below the prescribed requirement.`,
                        'This letter serves as a second warning and indicates that further non-compliance with the attendance policy may lead to academic penalties.',
                        'You are strongly advised to rectify this situation immediately and ensure consistent attendance going forward.'
                    ];
                case 'attendance-third-warning':
                    return [
                        `This letter is issued as a third and final warning regarding the poor attendance record of ${name} (Student ID: ${id}) in ${courseName} (Reg. No: ${regNumber}).`,
                        'Please note that failure to demonstrate immediate and sustained improvement in attendance may result in cancellation of registration or other disciplinary action as per institutional regulations.',
                        'You are requested to treat this matter with utmost seriousness and comply with the attendance requirements without further delay.'
                    ];
                case 'attendance-batch-transfer':
                    return [
                        `This is to inform you that, due to inadequate attendance in ${courseName} (Reg. No: ${regNumber}), the registration of ${name} (Student ID: ${id}) is being considered for transfer to a subsequent batch.`,
                        'The purpose of this transfer is to provide an opportunity to repeat missed components and to meet the minimum academic and attendance requirements of the programme.',
                        'Further details regarding the proposed batch transfer, including schedule and conditions, will be communicated by the course coordinator or administrative office.'
                    ];
                case 'submission-first-warning':
                    return [
                        `It has been noted that ${name} (Student ID: ${id}) has failed to submit one or more required assessments in ${courseName} (Reg. No: ${regNumber}) within the stipulated deadlines.`,
                        'This letter serves as the first formal reminder to complete and submit all pending coursework in order to avoid any adverse impact on overall academic performance.',
                        'You are advised to contact the relevant lecturer or course coordinator immediately to discuss pending submissions and revised timelines, if applicable.'
                    ];
                case 'submission-second-warning':
                    return [
                        `Our records indicate that, despite a previous warning, the required assessments for ${courseName} (Reg. No: ${regNumber}) by ${name} (Student ID: ${id}) still remain incomplete or overdue.`,
                        'This letter is issued as a second warning and emphasises that continued failure to submit the required work may lead to loss of marks or failure of the module as per regulations.',
                        'You are strongly urged to complete all outstanding submissions without further delay and to maintain regular communication with the academic staff regarding your progress.'
                    ];
                case 'submission-third-warning':
                    return [
                        `This letter serves as a third and final warning concerning the non-submission of mandatory assessments by ${name} (Student ID: ${id}) in ${courseName} (Reg. No: ${regNumber}).`,
                        'Please be aware that failure to submit the outstanding work immediately may result in serious academic consequences, including failure of the module or programme components.',
                        'You are requested to treat this matter as urgent and to comply fully with the submission requirements communicated by the institution.'
                    ];
                case 'submission-batch-transfer':
                    return [
                        `Due to repeated non-submission of required assessments in ${courseName} (Reg. No: ${regNumber}), the registration of ${name} (Student ID: ${id}) is being considered for transfer to a subsequent batch.`,
                        'This measure is intended to provide an opportunity to complete the missed assessments while adhering to the academic standards and assessment policies of the programme.',
                        'Further information regarding the batch transfer process, including conditions and timelines, will be shared by the course coordinator or administrative office.'
                    ];
                case 'job-recommendation':
                    return [
                        `This is to recommend ${name} (Student ID: ${id}), who has followed the course ${courseName} (Reg. No: ${regNumber}) at our institution.`,
                        'During the period of study, the student has demonstrated a positive attitude, a strong sense of responsibility, and the ability to work both independently and as part of a team.',
                        'We believe that ${name} will be a valuable addition to any organization and we wish them success in their future endeavours.'
                    ];
                case 'academic-recommendation':
                    return [
                        `I am pleased to recommend ${name} (Student ID: ${id}) for further academic studies based on the performance in ${courseName} (Reg. No: ${regNumber}).`,
                        'The student has consistently displayed commitment to learning, the capacity to understand complex concepts, and the motivation required for higher studies.',
                        'I strongly believe that ${name} will successfully complete the proposed academic programme and contribute positively to the academic community.'
                    ];
                case 'incident-report':
                    return [
                        `This letter is to formally record an incident involving ${name} (Student ID: ${id}) in relation to the course ${courseName} (Reg. No: ${regNumber}).`,
                        'Details of the incident have been documented and communicated to the relevant parties in accordance with institutional procedures.',
                        'This report will be kept on record and may be referred to in the event of any future disciplinary review.'
                    ];
                case 'disciplinary-notice':
                    return [
                        `This is a formal disciplinary notice issued to ${name} (Student ID: ${id}) with reference to the course ${courseName} (Reg. No: ${regNumber}).`,
                        'The student has been found to be in breach of the institution\'s rules and regulations, and appropriate action has been initiated as per the disciplinary code.',
                        'Further breaches may result in more severe action, including suspension or termination of studentship.'
                    ];
                case 'registered-inactivation':
                    return [
                        `Registered post notice informing ${name} (Student ID: ${id}) that the registration for ${courseName} (Reg. No: ${regNumber}) has been inactivated under ESOFT Metro Campus regulations.`,
                        'Directs the student to visit the campus within 10 working days, meet the course coordinator or HOD, and complete the required actions to reactivate the registration.',
                        'Clarifies that access to classes, LMS content, assignment and examination activities, and certificate services remains on hold until reactivation is completed.'
                    ];
                case 'registered-attendance':
                    return [
                        `Registered post reminder that attendance for ${courseName} (Reg. No: ${regNumber}) is below the required minimum despite previous advisories.`,
                        'Directs the student to attend upcoming sessions, submit supporting documents, and meet the coordinator within seven working days.',
                        'Warns that non-compliance can delay assessments, graduation processes, and may introduce extension fees.'
                    ];
                case 'registered-assignment':
                    return [
                        `Formal notice focusing on ${assignmentFocusLabel.toLowerCase()} for ${courseName} (Reg. No: ${regNumber}).`,
                        'Requests the student to complete the outstanding academic tasks promptly and provide the necessary lecturer acknowledgements or documentation.',
                        'Emphasizes that missed deadlines affect certificate requests, graduation eligibility, and could lead to additional extension fees.'
                    ];
                case 'registered-parent-meeting':
                    return [
                        `Invitation sent via registered post to the parent/guardian for a meeting regarding ${name} in relation to ${courseName}.`,
                        `Meeting date shared as ${parentMeetingDateLabel} with instructions to confirm attendance or request an alternate slot.`,
                        'Purpose is to review progress, attendance, and support required to keep the student in good standing.'
                    ];
                case 'hnd-pathway':
                    return [
                        `Reminder that the pathway verification deadline for ${courseName} (Reg. No: ${regNumber}) has already lapsed despite multiple notifications.`,
                        'Directs the student to visit the campus within 3 working days, meet the course coordinator or HOD, confirm the Software Engineering or Network Engineering pathway, and submit the signed form.',
                        'Warns that further delay will postpone certificates, graduation approvals, and could trigger extension fees if the registration expires before completion.'
                    ];
                default:
                    return [];
            }
        })();

        return (
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-800" id="letter-preview">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-xs text-slate-500">Date: {today}</p>
                        <p className="text-xs text-slate-500">Student: {name} ({id})</p>
                        <p className="text-xs text-slate-500">Email: {email}</p>
                    </div>
                    {selectedLetter && (
                        <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold uppercase text-slate-700">
                            {selectedLetter.replace('-', ' ')}
                        </span>
                    )}
                </div>
                <div className="space-y-3 leading-relaxed">
                    {paragraphs.map((p, idx) => (
                        <p key={idx}>{p}</p>
                    ))}
                </div>
            </div>
        );
    };

    const handlePrintLetter = () => {
        if (!selectedLetter || !selectedRegistrationId) return;
        window.print();
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Print Documents</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {student.fullName} ({student.studentId})
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 bg-slate-50">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-slate-600 text-slate-900 bg-white'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

                    {/* Course Selection, Tab Content + Letter Preview */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800">Registered Courses (coordinated by you)</h3>
                            {!coursesLoaded && (
                                <p className="mt-1 text-xs text-slate-500">Loading coordinated courses...</p>
                            )}
                            {coursesLoaded && coordinatedRegistrations.length === 0 && (
                                <p className="mt-1 text-xs text-slate-500">No registrations coordinated by you for this student.</p>
                            )}
                            {coursesLoaded && coordinatedRegistrations.length > 0 && (
                                <div className="mt-2 space-y-2">
                                    {coordinatedRegistrations.map((reg) => {
                                        const isSelected = selectedRegistrationId === reg.registrationId;

                                        const batchName = (reg.batchName || reg.batch || '').trim();
                                        const whatsappName = (reg.whatsappGroupName || reg.whatsappGroup || reg.whatsapp_group_id || '').trim();

                                        return (
                                            <button
                                                key={reg.registrationId}
                                                type="button"
                                                onClick={() => setSelectedRegistrationId(reg.registrationId)}
                                                className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-xs sm:text-sm transition-colors ${
                                                    isSelected
                                                        ? 'border-slate-600 bg-slate-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50'
                                                }`}
                                            >
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="font-semibold text-slate-800">
                                                        {reg.courseName || 'Course'}{batchName && ` (${batchName})`}
                                                    </span>
                                                    <span className="text-[11px] text-slate-600">Reg #: {reg.registrationNumber || 'N/A'}</span>
                                                    {whatsappName && (
                                                        <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                                                            <svg
                                                                className="h-3 w-3"
                                                                viewBox="0 0 32 32"
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    fill="currentColor"
                                                                    d="M16 3C9.383 3 4 8.383 4 15c0 2.247.627 4.334 1.72 6.137L4 29l8.137-1.7A11.86 11.86 0 0 0 16 27c6.617 0 12-5.383 12-12S22.617 3 16 3zm0 2c5.533 0 10 4.467 10 10s-4.467 10-10 10a9.8 9.8 0 0 1-3.938-.812l-.28-.118-4.812 1L8.3 20.3l-.145-.238A9.77 9.77 0 0 1 6 15c0-5.533 4.467-10 10-10zm-3.117 4.5c-.26 0-.672.097-1.023.485-.352.39-1.343 1.31-1.343 3.19 0 1.881 1.376 3.695 1.567 3.952.194.26 2.648 4.223 6.543 5.746 3.237 1.272 3.897 1.02 4.593.953.696-.065 2.255-.922 2.574-1.815.32-.89.32-1.653.225-1.815-.096-.162-.352-.258-.735-.452-.384-.195-2.255-1.11-2.604-1.237-.352-.13-.607-.195-.863.194-.257.39-.992 1.237-1.217 1.493-.225.259-.448.293-.83.098-.384-.195-1.62-.597-3.087-1.905-1.142-1.017-1.91-2.272-2.135-2.66-.224-.39-.024-.6.17-.793.175-.173.384-.45.576-.676.19-.227.255-.39.383-.649.129-.26.064-.487-.032-.683-.096-.195-.85-2.082-1.193-2.847-.315-.702-.642-.76-.902-.766z"
                                                                />
                                                            </svg>
                                                            <span>{whatsappName}</span>
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {reg.registrationStatus && (
                                                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                                                            {reg.registrationStatus}
                                                        </span>
                                                    )}
                                                    {reg.deliveryStatus && (
                                                        <span className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                                            {reg.deliveryStatus}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {renderTabContent()}
                        {renderLetterPreview()}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
                        <div className="flex justify-between gap-3">
                            <div className="flex items-center text-xs text-slate-500">
                                {selectedRegistrationId
                                    ? selectedLetter
                                        ? `Selected: ${selectedLetter.replace('-', ' ')} for chosen course`
                                        : 'Select a letter type for the chosen course.'
                                    : 'Select a course and letter type to preview.'}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="rounded-lg border-2 border-slate-300 bg-white px-6 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handlePrintLetter}
                                      disabled={!selectedLetter || !selectedRegistrationId}
                                    className="rounded-lg border-2 border-slate-600 bg-slate-600 px-6 py-2 text-sm font-semibold text-white hover:bg-slate-700 hover:border-slate-700 disabled:opacity-50 transition-colors"
                                >
                                    Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function StudentPassportPhoto({ student, onClick }) {
    const imgRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(student?.imagePath ? getImageURL(student.imagePath) : '');
    const [isLoading, setIsLoading] = useState(Boolean(student?.hasImage));
    const [usedFallback, setUsedFallback] = useState(false);

    const stopLoading = () => setIsLoading(false);

    useEffect(() => {
        if (student?.hasImage) {
            setImgSrc(getImageURL(student.imagePath));
            setIsLoading(true);
            setUsedFallback(false);
        } else {
            setImgSrc('');
            setIsLoading(false);
            setUsedFallback(true);
        }
    }, [student]);

    useEffect(() => {
        if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
            stopLoading();
        }
        const timer = setTimeout(stopLoading, 8000);
        return () => clearTimeout(timer);
    }, [imgSrc]);

    const handleError = () => {
        if (!usedFallback) {
            setImgSrc(PLACEHOLDER_IMAGE);
            setUsedFallback(true);
            stopLoading();
        }
    };

    const source = imgSrc || PLACEHOLDER_IMAGE;

    return (
        <div
            className={`relative h-28 w-20 overflow-hidden rounded-md border border-gray-200 bg-gray-50 shadow-inner ${onClick ? 'cursor-zoom-in' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500"></div>
                </div>
            )}
            <img
                ref={imgRef}
                src={source}
                alt={student?.fullName || 'Student'}
                className="h-full w-full object-cover"
                loading="lazy"
                onLoad={stopLoading}
                onError={handleError}
            />
        </div>
    );
}

function StudentImageLightbox({ student, onClose, onPrev, onNext, hasPrev, hasNext }) {
    const imgRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(student?.imagePath ? getImageURL(student.imagePath) : '');
    const [isLoading, setIsLoading] = useState(Boolean(student?.hasImage));
    const [usedFallback, setUsedFallback] = useState(false);

    const stopLoading = () => setIsLoading(false);

    useEffect(() => {
        if (student?.hasImage) {
            setImgSrc(getImageURL(student.imagePath));
            setIsLoading(true);
            setUsedFallback(false);
        } else {
            setImgSrc('');
            setIsLoading(false);
            setUsedFallback(true);
        }
    }, [student]);

    useEffect(() => {
        if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
            stopLoading();
        }
        const timer = setTimeout(stopLoading, 8000);
        return () => clearTimeout(timer);
    }, [imgSrc]);

    const handleError = () => {
        if (!usedFallback) {
            setImgSrc(PLACEHOLDER_IMAGE);
            setUsedFallback(true);
            stopLoading();
        }
    };

    const source = imgSrc || PLACEHOLDER_IMAGE;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <p className="text-sm font-semibold text-indigo-600">Student Photo</p>
                        <h3 className="text-xl font-bold text-gray-900">{student?.fullName || 'Student'}</h3>
                        <p className="text-sm text-gray-600">ID: {student?.studentId || 'N/A'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="relative flex items-center justify-center bg-gray-900">
                    {hasPrev && (
                        <button
                            onClick={onPrev}
                            className="absolute left-4 z-20 rounded-full bg-white/80 p-3 text-gray-800 shadow hover:bg-white"
                            aria-label="Previous image"
                        >
                            ‹
                        </button>
                    )}
                    {hasNext && (
                        <button
                            onClick={onNext}
                            className="absolute right-4 z-20 rounded-full bg-white/80 p-3 text-gray-800 shadow hover:bg-white"
                            aria-label="Next image"
                        >
                            ›
                        </button>
                    )}

                    <div className="relative max-h-[80vh] w-full max-w-3xl overflow-hidden bg-black">
                        {isLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40">
                                <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/40 border-t-indigo-400"></div>
                            </div>
                        )}
                        <img
                            ref={imgRef}
                            src={source}
                            alt={student?.fullName || 'Student'}
                            className="mx-auto max-h-[80vh] w-auto object-contain"
                            loading="lazy"
                            onLoad={stopLoading}
                            onError={handleError}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 text-sm text-gray-600">
                    <span>{student?.email || 'No email provided'}</span>
                    <div className="flex gap-2">
                        <button
                            onClick={onPrev}
                            disabled={!hasPrev}
                            className={`rounded-md px-3 py-2 font-semibold ${hasPrev ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' : 'cursor-not-allowed bg-gray-50 text-gray-400'}`}
                        >
                            Previous
                        </button>
                        <button
                            onClick={onNext}
                            disabled={!hasNext}
                            className={`rounded-md px-3 py-2 font-semibold ${hasNext ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' : 'cursor-not-allowed bg-gray-50 text-gray-400'}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AddStudentModal({ onClose, onSuccess, coordinateCourseIds = [], hasFullAccess }) {
    const [studentForm, setStudentForm] = useState({
        studentId: '',
        fullName: '',
        email: '',
        telephone: '',
        dob: '',
        gender: '',
        nicNumber: '',
        address: ''
    });
    const [registrationForm, setRegistrationForm] = useState({
        batchId: '',
        registrationNumber: '',
        verifiedName: ''
    });
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [batches, setBatches] = useState([]);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchError, setBatchError] = useState('');

    // Student ID availability check
    // status: 'idle' | 'checking' | 'available' | 'taken'
    const [studentIdStatus, setStudentIdStatus] = useState('idle');
    const [takenStudentInfo, setTakenStudentInfo] = useState(null); // { fullName, email }
    const studentIdCheckTimer = useRef(null);

    const normalizedCourseIds = useMemo(() =>
        Array.isArray(coordinateCourseIds)
            ? coordinateCourseIds.map((id) => id?.toString()).filter(Boolean)
            : []
    , [coordinateCourseIds]);

    const fetchAccessibleBatches = useCallback(async () => {
        setBatchLoading(true);
        setBatchError('');
        try {
            const { data } = await axiosInstance.get('/admin/batches');
            const rawBatches = data?.batches || [];
            const permittedBatches = hasFullAccess
                ? rawBatches
                : rawBatches.filter((batch) => {
                    const courseId = batch?.courseId ?? batch?.course?.courseId;
                    return courseId != null && normalizedCourseIds.includes(courseId.toString());
                });

            setBatches(permittedBatches);
            if (!hasFullAccess && normalizedCourseIds.length > 0 && permittedBatches.length === 0) {
                setBatchError('No batches are available for the courses you coordinate.');
            }
        } catch (err) {
            setBatchError(err.response?.data?.message || 'Failed to load batches.');
        } finally {
            setBatchLoading(false);
        }
    }, [hasFullAccess, normalizedCourseIds]);

    useEffect(() => {
        fetchAccessibleBatches();
    }, [fetchAccessibleBatches]);

    const handleStudentChange = (field, value) => {
        setStudentForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'fullName') {
            setRegistrationForm((prev) => (prev.verifiedName ? prev : { ...prev, verifiedName: value }));
        }
        if (field === 'studentId') {
            const trimmed = value.trim();
            if (studentIdCheckTimer.current) clearTimeout(studentIdCheckTimer.current);
            if (!trimmed) {
                setStudentIdStatus('idle');
                setTakenStudentInfo(null);
                return;
            }
            setStudentIdStatus('checking');
            setTakenStudentInfo(null);
            studentIdCheckTimer.current = setTimeout(async () => {
                try {
                    const { data } = await axiosInstance.get(
                        `/admin/students/${encodeURIComponent(trimmed)}/registration-status`
                    );
                    if (data.studentExists) {
                        setStudentIdStatus('taken');
                        setTakenStudentInfo(data.student ?? null);
                    } else {
                        setStudentIdStatus('available');
                        setTakenStudentInfo(null);
                    }
                } catch {
                    setStudentIdStatus('idle');
                    setTakenStudentInfo(null);
                }
            }, 600);
        }
    };

    const handleRegistrationChange = (field, value) => {
        setRegistrationForm((prev) => ({ ...prev, [field]: value }));
    };

    const batchOptions = useMemo(() =>
        batches.map((batch) => {
            const courseLabel = batch.course?.shortName || batch.course?.fullName || 'Course';
            const statusLabel = batch.status ? ` • ${batch.status}` : '';
            return {
                value: batch.batchId,
                label: `${batch.batchName} • ${courseLabel}${statusLabel}`
            };
        })
    , [batches]);

    const selectedBatchOption = useMemo(
        () => batchOptions.find((option) => option.value === registrationForm.batchId) || null,
        [batchOptions, registrationForm.batchId]
    );

    const accessibleSummary = hasFullAccess
        ? 'Full access to all courses'
        : normalizedCourseIds.length > 0
            ? `${normalizedCourseIds.length} coordinated course${normalizedCourseIds.length === 1 ? '' : 's'}`
            : 'No coordinated courses assigned yet';

    const sanitize = (value) => (typeof value === 'string' ? value.trim() : '');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        const trimmedStudentId = sanitize(studentForm.studentId);
        const trimmedFullName = sanitize(studentForm.fullName);
        const trimmedRegNo = sanitize(registrationForm.registrationNumber);
        const verifiedName = sanitize(registrationForm.verifiedName) || trimmedFullName;

        if (!trimmedStudentId) {
            setFormError('Student ID is required.');
            return;
        }
        if (!trimmedFullName) {
            setFormError('Full name is required.');
            return;
        }
        if (!registrationForm.batchId) {
            setFormError('Please select a batch for the initial registration.');
            return;
        }
        if (!trimmedRegNo) {
            setFormError('Registration number is required.');
            return;
        }
        if (!hasFullAccess && normalizedCourseIds.length === 0) {
            setFormError('You do not have any coordinated courses assigned.');
            return;
        }
        if (batches.length === 0) {
            setFormError('No accessible batches available for registration.');
            return;
        }

        const studentPayload = {
            studentId: trimmedStudentId,
            fullName: trimmedFullName,
            email: sanitize(studentForm.email),
            telephone: sanitize(studentForm.telephone),
            dob: studentForm.dob || '',
            gender: studentForm.gender || '',
            nicNumber: sanitize(studentForm.nicNumber),
            address: sanitize(studentForm.address)
        };

        Object.keys(studentPayload).forEach((key) => {
            if (studentPayload[key] === '') {
                delete studentPayload[key];
            }
        });

        try {
            setSubmitting(true);
            await axiosInstance.post('/admin/students', studentPayload);
            await axiosInstance.post('/admin/registrations', {
                studentId: trimmedStudentId,
                batchId: registrationForm.batchId,
                registrationNumber: trimmedRegNo,
                verifiedName,
                registrationStatus: 'active'
            });

            await Swal.fire({
                title: 'Student Added',
                text: 'Student profile created and registered successfully.',
                icon: 'success',
                confirmButtonColor: '#0f172a'
            });

            if (typeof onSuccess === 'function') {
                onSuccess();
            } else {
                onClose();
            }
        } catch (err) {
            setFormError(err.response?.data?.message || 'Failed to add student.');
        } finally {
            setSubmitting(false);
        }
    };

    const disableSubmit = submitting || !studentForm.studentId.trim() || !studentForm.fullName.trim() || !registrationForm.batchId || !registrationForm.registrationNumber.trim() || studentIdStatus === 'taken' || studentIdStatus === 'checking';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600">New student</p>
                        <h2 className="text-2xl font-bold text-slate-900">Add Student & Register</h2>
                        <p className="text-sm text-slate-600">Create a student record and connect it to a coordinated batch.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                        disabled={submitting}
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="max-h-[calc(100vh-180px)] overflow-y-auto px-6 py-5 space-y-5">
                    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-sky-50 via-indigo-50 to-violet-50 p-4 text-slate-800">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-slate-500">Access scope</p>
                                <p className="text-lg font-bold text-slate-900">{accessibleSummary}</p>
                                <p className="text-sm text-slate-600">{hasFullAccess ? 'You can pick any batch from the catalog.' : 'Only batches from your coordinated courses will appear.'}</p>
                            </div>
                            <div className="rounded-xl bg-white px-4 py-2 text-center shadow-sm">
                                <p className="text-xs font-semibold text-slate-500">Available batches</p>
                                <p className="text-2xl font-bold text-indigo-600">{batchLoading ? '...' : batches.length}</p>
                            </div>
                        </div>
                    </div>

                    {(formError || batchError) && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 space-y-1">
                            {[formError, batchError]
                                .filter(Boolean)
                                .map((message, idx) => (
                                    <p key={idx}>{message}</p>
                                ))}
                        </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="mb-4 flex items-center gap-2">
                            <div className="rounded-lg bg-slate-900/90 p-2 text-white">
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 10-6 0 3 3 0 006 0zM12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 1</p>
                                <h3 className="text-lg font-bold text-slate-900">Student details</h3>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Student ID <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={studentForm.studentId}
                                        onChange={(e) => handleStudentChange('studentId', e.target.value)}
                                        placeholder="e.g., STU2024-001"
                                        className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white pr-9 ${
                                            studentIdStatus === 'taken'
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                                : studentIdStatus === 'available'
                                                ? 'border-green-400 focus:border-green-500 focus:ring-green-500/20'
                                                : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20'
                                        }`}
                                    />
                                    {/* Right-side icon */}
                                    {studentIdStatus === 'checking' && (
                                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                            <svg className="h-4 w-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                            </svg>
                                        </div>
                                    )}
                                    {studentIdStatus === 'available' && (
                                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                            <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                    {studentIdStatus === 'taken' && (
                                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                            <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                {/* Status messages */}
                                {studentIdStatus === 'checking' && (
                                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                                        <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                        Checking availability…
                                    </p>
                                )}
                                {studentIdStatus === 'available' && (
                                    <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-green-600">
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Student ID is available
                                    </p>
                                )}
                                {studentIdStatus === 'taken' && (
                                    <div className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                        <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
                                            <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                            </svg>
                                            This Student ID is already registered
                                        </p>
                                        {takenStudentInfo?.fullName && (
                                            <p className="mt-0.5 pl-5 text-xs text-red-600">
                                                Belongs to: <span className="font-medium">{takenStudentInfo.fullName}</span>
                                                {takenStudentInfo.email ? ` · ${takenStudentInfo.email}` : ''}
                                            </p>
                                        )}
                                        <p className="mt-0.5 pl-5 text-xs text-red-500">Please use a unique Student ID for each student.</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Full Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={studentForm.fullName}
                                    onChange={(e) => handleStudentChange('fullName', e.target.value)}
                                    placeholder="Enter full name"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Email</label>
                                <input
                                    type="email"
                                    value={studentForm.email}
                                    onChange={(e) => handleStudentChange('email', e.target.value)}
                                    placeholder="student@email.com"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Telephone</label>
                                <input
                                    type="tel"
                                    value={studentForm.telephone}
                                    onChange={(e) => handleStudentChange('telephone', e.target.value)}
                                    placeholder="071 123 4567"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Date of Birth</label>
                                <input
                                    type="date"
                                    value={studentForm.dob}
                                    onChange={(e) => handleStudentChange('dob', e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Gender</label>
                                <Select
                                    value={studentForm.gender ? { value: studentForm.gender, label: studentForm.gender } : null}
                                    onChange={(option) => handleStudentChange('gender', option?.value || '')}
                                    options={[
                                        { value: 'Male', label: 'Male' },
                                        { value: 'Female', label: 'Female' },
                                        { value: 'Other', label: 'Other' }
                                    ]}
                                    placeholder="Select gender"
                                    isClearable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '0.75rem',
                                            borderColor: '#cbd5f5',
                                            '&:hover': { borderColor: '#6366f1' },
                                            boxShadow: 'none'
                                        })
                                    }}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-sm font-semibold text-slate-700">NIC Number</label>
                                <input
                                    type="text"
                                    value={studentForm.nicNumber}
                                    onChange={(e) => handleStudentChange('nicNumber', e.target.value)}
                                    placeholder="NIC / Passport"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Address</label>
                                <textarea
                                    rows={3}
                                    value={studentForm.address}
                                    onChange={(e) => handleStudentChange('address', e.target.value)}
                                    placeholder="Residential address"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border-2 border-indigo-200 bg-white p-5">
                        <div className="mb-4 flex items-center gap-2">
                            <div className="rounded-lg bg-indigo-600/90 p-2 text-white">
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Step 2</p>
                                <h3 className="text-lg font-bold text-slate-900">Registration details</h3>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Select Batch <span className="text-red-500">*</span></label>
                                <Select
                                    value={selectedBatchOption}
                                    onChange={(option) => handleRegistrationChange('batchId', option?.value || '')}
                                    options={batchOptions}
                                    placeholder={batchLoading ? 'Loading batches...' : batchOptions.length ? 'Pick a batch' : 'No batches available'}
                                    isDisabled={batchLoading || batchOptions.length === 0}
                                    isSearchable
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            borderRadius: '0.75rem',
                                            borderColor: state.isFocused ? '#4f46e5' : '#c7d2fe',
                                            boxShadow: 'none'
                                        })
                                    }}
                                />
                                {!batchLoading && batchOptions.length > 0 && (
                                    <p className="mt-1 text-xs text-slate-500">{batchOptions.length} batch option{batchOptions.length === 1 ? '' : 's'} available.</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-700">Registration Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={registrationForm.registrationNumber}
                                        onChange={(e) => handleRegistrationChange('registrationNumber', e.target.value)}
                                        placeholder="CMS/2024/001"
                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-700">Verified Name</label>
                                    <input
                                        type="text"
                                        value={registrationForm.verifiedName}
                                        onChange={(e) => handleRegistrationChange('verifiedName', e.target.value)}
                                        placeholder="Official verified name"
                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl border border-slate-300 bg-white px-6 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-600 bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={disableSubmit}
                        >
                            {submitting ? (
                                <>
                                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Save & Register
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const formatBatchLabel = (batch) => {
    if (!batch) return 'Batch';
    const fallbackName = batch.batchId ? `Batch ${batch.batchId}` : 'Batch';
    const batchName = batch.batchName || batch.batchInternalName || fallbackName;
    const courseLabel = batch.course?.shortName || batch.course?.fullName || batch.courseName || '';
    const statusSuffix = batch.status ? ` (${batch.status})` : '';
    return courseLabel ? `${batchName} • ${courseLabel}${statusSuffix}` : `${batchName}${statusSuffix}`;
};

const formatRegistrationBatchLabel = (registration) => {
    if (!registration) return 'Batch';
    const fallbackName = registration.batchId ? `Batch ${registration.batchId}` : `Batch ${registration.registrationId}`;
    const batchName = registration.batchName || registration.batchInternalName || fallbackName;
    const courseLabel = registration.courseName ? ` • ${registration.courseName}` : '';
    return `${batchName}${courseLabel}`;
};

// Register Student Modal Component
export function RegisterStudentModal({ student, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [batches, setBatches] = useState([]);
    const [existingRegistrations, setExistingRegistrations] = useState([]);
    const [editableCourseIds, setEditableCourseIds] = useState([]);
    const [deletableCourseIds, setDeletableCourseIds] = useState([]);
    const [editingRegistrationId, setEditingRegistrationId] = useState(null);
    const [editForm, setEditForm] = useState({
        registrationNumber: '',
        batchId: '',
        nameVerification: '',
        stream: '',
        universityID: '',
        universityRegisterDate: ''
    });
    const [formData, setFormData] = useState({
        studentId: student.studentId,
        fullName: student.fullName,
        batchId: '',
        registrationNumber: '',
        verifiedName: student.fullName
    });
    const [error, setError] = useState('');
    const currentUser = AuthService.getUser();

    const batchMap = useMemo(() => {
        const map = new Map();
        (batches || []).forEach((batch) => {
            const id = Number(batch.batchId);
            if (Number.isFinite(id)) {
                map.set(id, batch);
            }
        });
        return map;
    }, [batches]);

    const batchSelectOptions = useMemo(() => {
        return (batches || []).map((batch) => ({
            value: batch.batchId,
            label: formatBatchLabel(batch)
        }));
    }, [batches]);

    useEffect(() => {
        fetchBatches();
        fetchExistingRegistrations();
        fetchAssignedCourses();
    }, []);

    const fetchBatches = async () => {
        try {
            const response = await axiosInstance.get('/admin/batches');
            setBatches(response.data.batches || []);
        } catch (err) {
            console.error('Failed to fetch batches:', err);
            setError('Failed to load batches');
        }
    };

    const fetchExistingRegistrations = async () => {
        try {
            // Fetch fresh registration data from the server
            const response = await axiosInstance.get(`/admin/students/${student.studentId}`);
            const registrations = response.data?.student?.registrations || [];
            setExistingRegistrations(registrations);
        } catch (err) {
            console.error('Failed to fetch registrations:', err);
            // Fallback to prop data if API fails
            setExistingRegistrations(student.registrations || []);
        }
    };

    const fetchAssignedCourses = async () => {
        try {
            const response = await axiosInstance.get('/admin/me/coordinate-courses');
            const coordinateCourses = response.data?.coordinateCourses || {};
            const coursesList = Object.values(coordinateCourses);

            // Edit and delete are gated separately so a coordinator only sees
            // the specific action their coordinate_courses permission actually
            // allows (a coordinator can have isUpdate without isDelete, or vice versa).
            setEditableCourseIds(coursesList.filter((c) => c.isUpdate).map((c) => Number(c.courseId)));
            setDeletableCourseIds(coursesList.filter((c) => c.isDelete).map((c) => Number(c.courseId)));
        } catch (err) {
            console.error('Failed to fetch coordinate courses:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.batchId) {
            setError('Please select a batch');
            return;
        }
        
        if (!formData.registrationNumber.trim()) {
            setError('Please enter a registration number');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            await axiosInstance.post('/admin/registrations', {
                studentId: formData.studentId,
                batchId: formData.batchId,
                registrationNumber: formData.registrationNumber,
                verifiedName: formData.verifiedName,
                registrationStatus: 'active'
            });
            
            // Refresh the registrations list to show the new registration
            await fetchExistingRegistrations();
            
            // Clear the form
            setFormData({
                ...formData,
                batchId: '',
                registrationNumber: '',
                verifiedName: student.fullName
            });
            
            await Swal.fire({
                title: 'Registered!',
                text: 'Student has been successfully registered to the course.',
                icon: 'success',
                confirmButtonColor: '#64748b'
            });
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to register student');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRegistration = async (registrationId) => {
        const result = await Swal.fire({
            title: 'Delete Registration?',
            text: 'This action cannot be undone. The registration will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
            return;
        }
        
        try {
            await axiosInstance.delete(`/admin/registrations/${registrationId}`);
            await Swal.fire({
                title: 'Deleted!',
                text: 'Registration has been successfully deleted.',
                icon: 'success',
                confirmButtonColor: '#64748b'
            });
            // Remove from local state so UI reflects immediately
            setExistingRegistrations((prev) => prev.filter((r) => r.registrationId !== registrationId));
            // Also inform parent to refresh overall list
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to delete registration',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        }
    };

    const handleStartEdit = (reg) => {
        setEditingRegistrationId(reg.registrationId);
        
        // Format universityRegisterDate for date input (YYYY-MM-DD)
        let formattedDate = '';
        if (reg.universityRegisterDate) {
            const date = new Date(reg.universityRegisterDate);
            if (!isNaN(date.getTime())) {
                formattedDate = date.toISOString().split('T')[0];
            }
        }
        
        setEditForm({
            registrationNumber: reg.registrationNumber || '',
            batchId: reg.batchId || '',
            nameVerification: reg.nameVerification || student.fullName,
            stream: reg.stream || '',
            universityID: reg.universityID || '',
            universityRegisterDate: formattedDate
        });
    };

    const handleCancelEdit = () => {
        setEditingRegistrationId(null);
        setEditForm({
            registrationNumber: '',
            batchId: '',
            nameVerification: '',
            stream: '',
            universityID: '',
            universityRegisterDate: ''
        });
    };

    const handleSaveEdit = async (registrationId) => {
        const reg = existingRegistrations.find((r) => r.registrationId === registrationId);
        if (!reg) {
            return;
        }

        const trimmedRegistrationNumber = (editForm.registrationNumber || '').trim();
        if (!trimmedRegistrationNumber) {
            await Swal.fire({
                title: 'Registration number required',
                text: 'Please provide a registration number before saving changes.',
                icon: 'info',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        const toNumber = (value) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : null;
        };

        const currentBatchId = toNumber(reg.batchId);
        const editedBatchId = editForm.batchId !== '' && editForm.batchId !== null ? toNumber(editForm.batchId) : null;
        const normalizedBatchId = editedBatchId !== null ? editedBatchId : currentBatchId;
        const batchChanged = normalizedBatchId !== null && normalizedBatchId !== currentBatchId;
        const resolvedBatch = normalizedBatchId !== null ? batchMap.get(normalizedBatchId) : null;
        const resolvedCourseName = (
            resolvedBatch?.course?.fullName ||
            resolvedBatch?.course?.shortName ||
            resolvedBatch?.courseName ||
            reg.courseName ||
            ''
        ).toLowerCase();
        const isHndCourse = resolvedCourseName.includes('hnd in computing');

        const updateData = {
            registrationNumber: trimmedRegistrationNumber,
            nameVerification: editForm.nameVerification,
            universityID: editForm.universityID,
            universityRegisterDate: editForm.universityRegisterDate
        };

        if (batchChanged && normalizedBatchId !== null) {
            updateData.batchId = normalizedBatchId;
        }

        if (isHndCourse) {
            updateData.stream = editForm.stream || '';
        } else if (batchChanged && reg.stream) {
            updateData.stream = '';
        }

        try {
            setLoading(true);
            const { data } = await axiosInstance.put(`/admin/registrations/${registrationId}`, updateData);
            setEditingRegistrationId(null);
            await Swal.fire({
                title: 'Updated!',
                text: 'Registration has been successfully updated.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            setExistingRegistrations((prev) => prev.map((r) => {
                if (r.registrationId !== registrationId) return r;
                const srv = data?.registration;
                if (srv) {
                    return {
                        ...r,
                        registrationNumber: srv.registrationNumber ?? r.registrationNumber,
                        registrationStatus: srv.registrationStatus ?? r.registrationStatus,
                        nameVerification: srv.nameVerification ?? r.nameVerification,
                        deliveryStatus: srv.deliveryStatus ?? r.deliveryStatus,
                        stream: srv.stream ?? r.stream,
                        courseId: srv.courseId ?? r.courseId,
                        courseName: srv.courseName ?? r.courseName,
                        batchId: srv.batchId ?? r.batchId,
                        batchName: srv.batchName ?? r.batchName,
                        universityID: srv.universityID ?? r.universityID,
                        universityRegisterDate: srv.universityRegisterDate ?? r.universityRegisterDate,
                    };
                }

                const fallbackBatchId = Object.prototype.hasOwnProperty.call(updateData, 'batchId') ? updateData.batchId : r.batchId;
                const fallbackBatchIdNumber = toNumber(fallbackBatchId);
                const fallbackBatch = fallbackBatchIdNumber !== null ? batchMap.get(fallbackBatchIdNumber) : null;

                return {
                    ...r,
                    registrationNumber: updateData.registrationNumber ?? r.registrationNumber,
                    nameVerification: updateData.nameVerification ?? r.nameVerification,
                    stream: Object.prototype.hasOwnProperty.call(updateData, 'stream')
                        ? updateData.stream || ''
                        : r.stream,
                    universityID: updateData.universityID ?? r.universityID,
                    universityRegisterDate: updateData.universityRegisterDate ?? r.universityRegisterDate,
                    batchId: Object.prototype.hasOwnProperty.call(updateData, 'batchId') ? updateData.batchId : r.batchId,
                    batchName: fallbackBatch ? fallbackBatch.batchName : r.batchName,
                    courseId: fallbackBatch
                        ? (fallbackBatch.courseId ?? fallbackBatch.course?.courseId ?? r.courseId)
                        : r.courseId,
                    courseName: fallbackBatch
                        ? (fallbackBatch.course?.fullName || fallbackBatch.course?.shortName || fallbackBatch.courseName || r.courseName)
                        : r.courseName,
                };
            }));

            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to update registration',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
            <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl my-8">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">Register Student</h2>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {/* Already Registered Courses */}
                    {existingRegistrations.length > 0 && (
                        <div className="rounded-lg border-2 border-slate-300 bg-slate-50 p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <svg className="h-5 w-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                </svg>
                                <h3 className="font-bold text-slate-900">Already Registered Courses</h3>
                            </div>
                            <div className="space-y-3">
                                {existingRegistrations.map((reg) => {
                                    const regCourseId = Number(reg.courseId);
                                    const canEdit = editableCourseIds.includes(regCourseId);
                                    const canDelete = deletableCourseIds.includes(regCourseId);
                                    const canManage = canEdit || canDelete;
                                    const isEditing = editingRegistrationId === reg.registrationId;
                                    let editingBatchOption = null;
                                    let editingStreamVisible = false;
                                    if (isEditing) {
                                        const rawBatchId = editForm.batchId !== '' && editForm.batchId !== null
                                            ? Number(editForm.batchId)
                                            : Number(reg.batchId);
                                        const normalizedBatchId = Number.isFinite(rawBatchId) ? rawBatchId : null;
                                        const batchForEdit = normalizedBatchId !== null ? batchMap.get(normalizedBatchId) : null;
                                        if (batchForEdit) {
                                            editingBatchOption = { value: batchForEdit.batchId, label: formatBatchLabel(batchForEdit) };
                                            const courseName =
                                                batchForEdit.course?.fullName ||
                                                batchForEdit.course?.shortName ||
                                                batchForEdit.courseName ||
                                                reg.courseName ||
                                                '';
                                            editingStreamVisible = courseName.toLowerCase().includes('hnd in computing');
                                        } else if (reg.batchId) {
                                            editingBatchOption = { value: reg.batchId, label: formatRegistrationBatchLabel(reg) };
                                            editingStreamVisible = (reg.courseName || '').toLowerCase().includes('hnd in computing');
                                        } else {
                                            editingStreamVisible = (reg.courseName || '').toLowerCase().includes('hnd in computing');
                                        }
                                    }
                                    
                                    return (
                                        <div key={reg.registrationId} className="rounded-lg border border-slate-300 bg-white p-4">
                                            {isEditing ? (
                                                // Edit Mode
                                                <div className="space-y-3">
                                                    <h4 className="font-semibold text-gray-900">{reg.courseName || 'Course'}</h4>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Registration Number</label>
                                                        <input
                                                            type="text"
                                                            value={editForm.registrationNumber}
                                                            onChange={(e) => setEditForm({ ...editForm, registrationNumber: e.target.value })}
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Registered Batch</label>
                                                        <Select
                                                            value={editingBatchOption}
                                                            onChange={(option) => setEditForm({ ...editForm, batchId: option?.value || '' })}
                                                            options={batchSelectOptions}
                                                            placeholder={batchSelectOptions.length ? 'Select batch' : 'No batches available'}
                                                            isDisabled={!batchSelectOptions.length}
                                                            isSearchable
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: '32px',
                                                                    borderRadius: '0.25rem',
                                                                    borderColor: '#d1d5db',
                                                                    fontSize: '0.875rem',
                                                                    '&:hover': { borderColor: '#64748b' },
                                                                    '&:focus': { borderColor: '#64748b', boxShadow: '0 0 0 1px rgba(100, 116, 139, 0.2)' }
                                                                }),
                                                                valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
                                                                input: (base) => ({ ...base, margin: 0, padding: 0 })
                                                            }}
                                                        />
                                                        <p className="mt-1 text-[11px] text-gray-500">
                                                            Switching batches moves this registration to the selected course.
                                                        </p>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Verified Name</label>
                                                        <input
                                                            type="text"
                                                            value={editForm.nameVerification}
                                                            onChange={(e) => setEditForm({ ...editForm, nameVerification: e.target.value })}
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>

                                                    {editingStreamVisible && (
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">Stream</label>
                                                            <Select
                                                                value={editForm.stream ? { value: editForm.stream, label: editForm.stream } : null}
                                                                onChange={(option) => setEditForm({ ...editForm, stream: option?.value || '' })}
                                                                options={[
                                                                    { value: 'Software Engineering', label: 'Software Engineering' },
                                                                    { value: 'Network Engineering', label: 'Network Engineering' }
                                                                ]}
                                                                placeholder="-- Select Stream --"
                                                                isClearable
                                                                isSearchable
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: '32px',
                                                                        borderRadius: '0.25rem',
                                                                        borderColor: '#d1d5db',
                                                                        fontSize: '0.875rem',
                                                                        '&:hover': { borderColor: '#64748b' },
                                                                        '&:focus': { borderColor: '#64748b', boxShadow: '0 0 0 1px rgba(100, 116, 139, 0.2)' }
                                                                    }),
                                                                    valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
                                                                    input: (base) => ({ ...base, margin: 0, padding: 0 })
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">University ID</label>
                                                        <input
                                                            type="text"
                                                            value={editForm.universityID}
                                                            onChange={(e) => setEditForm({ ...editForm, universityID: e.target.value })}
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="Enter university ID"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">University Register Date</label>
                                                        <input
                                                            type="date"
                                                            value={editForm.universityRegisterDate}
                                                            onChange={(e) => setEditForm({ ...editForm, universityRegisterDate: e.target.value })}
                                                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>

                                                    <div className="flex gap-2 pt-2">
                                                        <button
                                                            onClick={() => handleSaveEdit(reg.registrationId)}
                                                            disabled={loading}
                                                            className="inline-flex items-center gap-1.5 rounded border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                                                        >
                                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            disabled={loading}
                                                            className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // View Mode
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-gray-900">{reg.courseName || 'Course'}</h4>
                                                        <p className="text-sm text-gray-600 mt-1">Reg #: {reg.registrationNumber}</p>
                                                        {reg.nameVerification && (
                                                            <p className="text-xs text-gray-500 mt-0.5">Verified: {reg.nameVerification}</p>
                                                        )}
                                                        {reg.universityID && (
                                                            <p className="text-xs text-gray-500 mt-0.5">University ID: {reg.universityID}</p>
                                                        )}
                                                        {reg.universityRegisterDate && (() => {
                                                            const regDate = new Date(reg.universityRegisterDate);
                                                            const today = new Date();
                                                            const monthsPassed = (today.getFullYear() - regDate.getFullYear()) * 12 + (today.getMonth() - regDate.getMonth());
                                                            return (
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    University Register Date: {regDate.toLocaleDateString()} ({monthsPassed} {monthsPassed === 1 ? 'month' : 'months'} ago)
                                                                </p>
                                                            );
                                                        })()}
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            <span className="inline-flex items-center rounded-full border border-green-300 bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                                                                {reg.registrationStatus || 'active'}
                                                            </span>
                                                            <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                                                                {reg.deliveryStatus ? reg.deliveryStatus : 'Ongoing'}
                                                            </span>
                                                            {canManage && (
                                                                <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                                                                    Coordinated
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {canManage && (
                                                        <div className="flex gap-2">
                                                            {canEdit && (
                                                                <button
                                                                    onClick={() => handleStartEdit(reg)}
                                                                    className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                                                >
                                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                    Edit
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button
                                                                    onClick={() => handleDeleteRegistration(reg.registrationId)}
                                                                    className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors"
                                                                >
                                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Student Information */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <svg className="h-5 w-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <h3 className="font-bold text-gray-900">Student Information</h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Student ID</label>
                                <input
                                    type="text"
                                    value={formData.studentId}
                                    disabled
                                    className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                />
                                <p className="mt-1 text-xs text-gray-500">You can modify the name if needed</p>
                            </div>
                        </div>
                    </div>

                    {/* Registration Details */}
                    <form onSubmit={handleSubmit} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <svg className="h-5 w-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                            </svg>
                            <h3 className="font-bold text-slate-900">Registration Details</h3>
                        </div>
                        
                        {error && (
                            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                                {error}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Select Batch <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={(() => {
                                        const numericId = Number(formData.batchId);
                                        if (!Number.isFinite(numericId)) return null;
                                        const selected = batchMap.get(numericId);
                                        if (!selected) return null;
                                        return {
                                            value: selected.batchId,
                                            label: formatBatchLabel(selected)
                                        };
                                    })()}
                                    onChange={(option) => setFormData({ ...formData, batchId: option?.value || '' })}
                                    options={batchSelectOptions}
                                    placeholder="-- Select a Batch --"
                                    isClearable
                                    isSearchable
                                    required
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '0.5rem',
                                            borderColor: '#d1d5db',
                                            '&:hover': { borderColor: '#64748b' },
                                            '&:focus': { borderColor: '#64748b', boxShadow: '0 0 0 2px rgba(100, 116, 139, 0.2)' }
                                        })
                                    }}
                                />
                                <p className="mt-1 text-xs text-gray-600">{batchSelectOptions.length} batch(es) available</p>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Registration Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.registrationNumber}
                                    onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                                    placeholder="Enter registration number"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Verified Name</label>
                                <input
                                    type="text"
                                    value={formData.verifiedName}
                                    onChange={(e) => setFormData({ ...formData, verifiedName: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                                <p className="mt-1 text-xs text-gray-600">Name as verified for official records</p>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-600 bg-slate-600 px-6 py-2 text-sm font-semibold text-white hover:bg-slate-700 hover:border-slate-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Registering...
                                    </>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Register Student
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

// Update Student Modal Component
export function UpdateStudentModal({ student, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        studentId: student.studentId || '',
        fullName: student.fullName || '',
        email: student.email || '',
        telephone: student.telephone || '',
        dob: student.dob || '',
        gender: student.gender || '',
        nicNumber: student.nicNumber || '',
        address: student.address || ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const result = await Swal.fire({
            title: 'Confirm Update',
            text: 'Are you sure you want to update this student\'s information?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#64748b',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            buttonsStyling: true
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);

        try {
            await axiosInstance.put(`/admin/students/${student.studentId}`, formData);
            await Swal.fire({
                title: 'Success!',
                text: 'Student information has been successfully updated.',
                icon: 'success',
                confirmButtonColor: '#64748b'
            });
            onSuccess();
        } catch (err) {
            console.error('Update error:', err);
            setError(err.response?.data?.message || 'Failed to update student');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
            <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl my-8">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Update Student Information</h2>
                            <p className="text-sm text-slate-600 mt-1">Student ID: {student.studentId}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {error && (
                        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800">
                            {error}
                        </div>
                    )}

                    {/* Personal Information */}
                    <div className="rounded-lg border-2 border-slate-300 bg-slate-50 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <svg className="h-5 w-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <h3 className="font-bold text-slate-900">Personal Information</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e) => handleChange('fullName', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Telephone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.telephone}
                                    onChange={(e) => handleChange('telephone', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Date of Birth
                                </label>
                                <input
                                    type="date"
                                    value={formData.dob}
                                    onChange={(e) => handleChange('dob', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Gender
                                </label>
                                <Select
                                    value={formData.gender ? { value: formData.gender, label: formData.gender } : null}
                                    onChange={(option) => handleChange('gender', option?.value || '')}
                                    options={[
                                        { value: 'Male', label: 'Male' },
                                        { value: 'Female', label: 'Female' },
                                        { value: 'Other', label: 'Other' }
                                    ]}
                                    placeholder="-- Select Gender --"
                                    isClearable
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '0.5rem',
                                            borderColor: '#d1d5db',
                                            '&:hover': { borderColor: '#64748b' },
                                            '&:focus': { borderColor: '#64748b', boxShadow: '0 0 0 2px rgba(100, 116, 139, 0.2)' }
                                        })
                                    }}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    NIC Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.nicNumber}
                                    onChange={(e) => handleChange('nicNumber', e.target.value)}
                                    placeholder="e.g., 123456789V or 201234567890"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Address
                                </label>
                                <textarea
                                    value={formData.address}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                    rows={3}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="rounded-lg border-2 border-gray-300 bg-white px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-600 bg-slate-600 px-6 py-2 text-sm font-semibold text-white hover:bg-slate-700 hover:border-slate-700 disabled:opacity-50 transition-colors"
                        >
                            {loading ? (
                                <>
                                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Update Student
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Reset Password Modal Component
export function ResetPasswordModal({ student, onClose, onSuccess, currentUser }) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Generate random 6-character password
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let randomPassword = '';
        for (let i = 0; i < 6; i++) {
            randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setPassword(randomPassword);
    }, []);

    const username = `${student.studentId}@esoft.academy`;

    const handleUpdatePassword = async () => {
        if (!password || password.length !== 6) {
            await Swal.fire({
                title: 'Invalid Password',
                text: 'Password must be exactly 6 characters',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.post(`/admin/students/${student.studentId}/reset-password`, {
                newPassword: password
            });

            await Swal.fire({
                title: 'Password Reset Successful',
                text: 'The password has been updated successfully.',
                icon: 'success',
                confirmButtonColor: '#64748b'
            });

            if (typeof onSuccess === 'function') {
                onSuccess();
            }
            onClose();
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to reset password',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCredentials = async () => {
        const userName = currentUser?.superAdmin?.staff?.name || currentUser?.superAdmin?.name || 'Administrator';
        const message = `📲 *MyC Mobile App Login Details* 📲

Dear ${student.fullName},

Here are your login credentials for the MyC Mobile App:

👤 *Username:* ${username}
🔑 *Password:* ${password}
🌐 *Login Link:* https://myc.kaaryaalaya.com/

Please keep this information secure and do not share it with anyone.

Best regards,  
 *${userName}*`;

        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            await Swal.fire({
                title: 'Copied!',
                text: 'Login credentials copied to clipboard',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
            setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: 'Failed to copy to clipboard',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-2xl">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
                            <p className="text-sm text-slate-600 mt-1">{student.fullName} ({student.studentId})</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="space-y-4">
                        {/* Username Display */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Username (Student ID)
                            </label>
                            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="flex-1 text-sm font-mono text-gray-900">{username}</span>
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                New Password
                            </label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                    <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value.slice(0, 6))}
                                        maxLength={6}
                                        className="flex-1 text-lg font-mono font-bold text-indigo-600 bg-transparent border-none outline-none"
                                        placeholder="6 chars"
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
                                        let randomPassword = '';
                                        for (let i = 0; i < 6; i++) {
                                            randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
                                        }
                                        setPassword(randomPassword);
                                    }}
                                    className="p-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                                    title="Generate new password"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Password must be exactly 6 characters</p>
                        </div>

                        {/* Info Box */}
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex gap-2">
                                <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="text-xs text-blue-800">
                                    <p className="font-semibold mb-1">MyC Mobile App Credentials</p>
                                    <p>Use the Copy button to copy formatted credentials that you can share with the student.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCopyCredentials}
                        disabled={!password || password.length !== 6}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                        onClick={handleUpdatePassword}
                        disabled={loading || !password || password.length !== 6}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? (
                            <>
                                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating...
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Update
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Reset Parent Password Modal Component
export function ResetParentPasswordModal({ student, onClose, onSuccess, currentUser }) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let randomPassword = '';
        for (let i = 0; i < 6; i++) {
            randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setPassword(randomPassword);
    }, []);

    const username = `${student.studentId}@esoft.academy`;

    const handleUpdatePassword = async () => {
        if (!password || password.length !== 6) {
            await Swal.fire({
                title: 'Invalid Password',
                text: 'Password must be exactly 6 characters',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.post(`/admin/students/${student.studentId}/reset-parent-password`, {
                newPassword: password
            });

            await Swal.fire({
                title: 'Parent Password Reset',
                text: 'The parent password has been updated successfully.',
                icon: 'success',
                confirmButtonColor: '#64748b'
            });

            if (typeof onSuccess === 'function') {
                onSuccess();
            }
            onClose();
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to reset parent password',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCredentials = async () => {
        const userName = currentUser?.superAdmin?.staff?.name || currentUser?.superAdmin?.name || 'Administrator';
        const message = `📲 *MyC Parent Access Details* 📲\n\nDear Parent/Guardian of ${student.fullName},\n\nHere are your login credentials to monitor your child's progress in the MyC Mobile App:\n\n👤 *Username:* ${username}\n🔑 *Parent Password:* ${password}\n🌐 *Login Link:* https://myc.kaaryaalaya.com/\n\nPlease keep this information secure and do not share it with anyone.\n\nBest regards,  \n *${userName}*`;

        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            await Swal.fire({
                title: 'Copied!',
                text: 'Parent login credentials copied to clipboard',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
            setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: 'Failed to copy to clipboard',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-2xl">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Reset Parent Password</h2>
                            <p className="text-sm text-slate-600 mt-1">{student.fullName} ({student.studentId})</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="space-y-4">
                        {/* Password Input */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                New Parent Password
                            </label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                                    <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value.slice(0, 6))}
                                        maxLength={6}
                                        className="flex-1 text-lg font-mono font-bold text-indigo-600 bg-transparent border-none outline-none"
                                        placeholder="6 chars"
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
                                        let randomPassword = '';
                                        for (let i = 0; i < 6; i++) {
                                            randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
                                        }
                                        setPassword(randomPassword);
                                    }}
                                    className="p-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                                    title="Generate new password"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Password must be exactly 6 characters</p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleCopyCredentials}
                        disabled={!password || password.length !== 6}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                        type="button"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-green-600 bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                        onClick={handleUpdatePassword}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating...
                            </>
                        ) : (
                            'Reset & Save'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RegistrationHistoryModal({ student, registration, onClose, refreshKey = 0, canAddHistory = true }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const registrationId = registration?.registrationId;

    const courseLabel = useMemo(() => {
        const batchName = (registration?.batchName || registration?.batch || '').trim();
        return `${registration?.courseName || 'Course'}${batchName ? ` (${batchName})` : ''}`;
    }, [registration]);

    const fetchHistory = useCallback(async () => {
        if (!registrationId) {
            setEntries([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const { data } = await axiosInstance.get(`/admin/registrations/${registrationId}/history`);
            const items =
                Array.isArray(data?.history) ? data.history :
                Array.isArray(data?.records) ? data.records :
                Array.isArray(data?.items) ? data.items :
                [];
            setEntries(items);
            setError(null);
        } catch (err) {
            console.error('Failed to load registration history', err);
            setError(err.response?.data?.message || 'Unable to load history right now.');
        } finally {
            setLoading(false);
        }
    }, [registrationId]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory, refreshKey]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsVisible(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const handleAddSuccess = useCallback(() => {
        setShowAddModal(false);
        fetchHistory();
    }, [fetchHistory]);

    const handleClose = useCallback(() => {
        setIsVisible(false);
        setTimeout(() => {
            onClose?.();
        }, 250);
    }, [onClose]);

    const sortedEntries = useMemo(() => {
        if (!Array.isArray(entries)) return [];
        const getTime = (entry) => {
            const raw = entry?.createdAt || entry?.created_at || entry?.timestamp || entry?.createdOn || entry?.created_on;
            const d = raw ? new Date(raw) : null;
            const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
            return t;
        };
        return [...entries].sort((a, b) => getTime(b) - getTime(a));
    }, [entries]);

    const formatDateTime = (value) => {
        if (!value) return 'Unknown date';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        // Display as YYYY-MM-DD to match example style
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            <div
                className={`flex-1 bg-black/60 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                onClick={handleClose}
            ></div>
            <div
                className={`flex h-full w-full max-w-md flex-col rounded-l-2xl bg-white shadow-2xl transition-transform duration-300 ease-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">Student History · {courseLabel}</h2>
                        <p className="mt-0.5 text-xs text-slate-600">
                            {student?.fullName} ({student?.studentId}) · Reg # {registration?.registrationNumber || 'N/A'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canAddHistory && (
                            <button
                                type="button"
                                onClick={() => setShowAddModal(true)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"
                                aria-label="Add history note"
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                            aria-label="Close history modal"
                        >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-10 text-slate-500">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-500" />
                            <p className="text-sm font-medium">Loading history...</p>
                        </div>
                    ) : error ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            {error}
                        </div>
                    ) : sortedEntries.length === 0 ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
                            No history records yet. Use the + button at the top of this window to add a note.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {sortedEntries.map((entry, idx) => {
                                const rawNote = entry?.note || entry?.notes || entry?.description || entry?.remark || entry?.comment || '';
                                const createdBy = entry?.createdByName || entry?.createdBy || entry?.updatedBy || 'System';

                                const backendTitle = entry?.title && String(entry.title).trim();
                                let title;
                                let body = '';
                                if (backendTitle) {
                                    title = backendTitle;
                                    body = String(rawNote).trim();
                                } else {
                                    const [firstLine, ...restLines] = String(rawNote).split(/\r?\n/);
                                    title = (firstLine || 'Note').trim();
                                    body = restLines.join('\n').trim();
                                }

                                const dateText = formatDateTime(entry?.createdAt || entry?.created_at || entry?.timestamp);

                                return (
                                    <div
                                        key={entry?.historyId || entry?.id || idx}
                                        className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                                    >
                                        <div className="text-sm font-semibold text-slate-800 line-clamp-2">{title}</div>
                                        {body ? (
                                            <div className="mt-1 text-xs text-slate-600 whitespace-pre-line">{body}</div>
                                        ) : null}
                                        {!body && rawNote ? (
                                            <div className="mt-1 text-xs text-slate-600 whitespace-pre-line">{rawNote}</div>
                                        ) : null}
                                        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                                            <span>{dateText}</span>
                                            <span className="italic">~ {createdBy}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    >
                        Close
                    </button>
                </div>
            </div>
            {showAddModal && (
                <AddRegistrationHistoryModal
                    student={student}
                    registration={registration}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={handleAddSuccess}
                />
            )}
        </div>
    );
}

function AddRegistrationHistoryModal({ student, registration, onClose, onSuccess }) {
    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [status, setStatus] = useState(registration?.registrationStatus || 'active');
    const [delivery, setDelivery] = useState(registration?.deliveryStatus || '');
    const [saving, setSaving] = useState(false);

    const registrationId = registration?.registrationId;

    const courseLabel = useMemo(() => {
        const batchName = (registration?.batchName || registration?.batch || '').trim();
        return `${registration?.courseName || 'Course'}${batchName ? ` (${batchName})` : ''}`;
    }, [registration]);

    const handleSave = async () => {
        const trimmedTitle = title.trim();
        const trimmedNote = note.trim();
        if (!trimmedTitle || !trimmedNote) {
            await Swal.fire({
                icon: 'info',
                title: 'Title and note required',
                text: 'Please enter both a title and note before saving.',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        if (!registrationId) {
            await Swal.fire({
                icon: 'error',
                title: 'Missing registration',
                text: 'History entries require a valid registration record.',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        try {
            setSaving(true);
            const payload = { title: trimmedTitle || null, note: trimmedNote || null };
            if (status) {
                payload.registrationStatus = status;
            }
            if (typeof delivery === 'string') {
                payload.deliveryStatus = delivery;
            }

            await axiosInstance.post(`/admin/registrations/${registrationId}/history`, payload);

            await Swal.fire({
                icon: 'success',
                title: 'Saved',
                text: 'History entry added successfully.',
                confirmButtonColor: '#16a34a'
            });

            if (typeof onSuccess === 'function') {
                onSuccess({ registrationId });
            } else {
                onClose?.();
            }
        } catch (err) {
            console.error('Failed to save registration history', err);
            await Swal.fire({
                icon: 'error',
                title: 'Save failed',
                text: err.response?.data?.message || 'Unable to add history right now.',
                confirmButtonColor: '#ef4444'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">Student History</h2>
                        <p className="mt-0.5 text-xs text-slate-600">
                            {student?.fullName} ({student?.studentId}) · {courseLabel}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                        aria-label="Close add history modal"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                    <label className="mb-1 block text-xs font-semibold text-slate-700">Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value.slice(0, 255))}
                        className="mb-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="e.g., Microsoft account created"
                    />

                    <label className="mb-1 block text-xs font-semibold text-slate-700">Notes</label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                        rows={8}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="e.g., Spoke with guardian, awaiting payment confirmation..."
                    />
                    <div className="mt-1 text-right text-xs text-slate-500">{note.length}/1000</div>
                </div>

                <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || (!title.trim() || !note.trim())}
                        className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Save Entry
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Students() {
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCourses, setSelectedCourses] = useState(['all']); // Array of selected course IDs or ['all']
    const [deliveryStatus, setDeliveryStatus] = useState('active');
    const [registrationStatus, setRegistrationStatus] = useState('active');
    const [showMissingPhotosOnly, setShowMissingPhotosOnly] = useState(false);
    const [courses, setCourses] = useState([]);
    const [error, setError] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [activeModal, setActiveModal] = useState(null); // 'details' | 'print' | 'image' | 'register' | 'update' | 'clean' | 'upload-image' | 'add-student'
    const [courseFilterModal, setCourseFilterModal] = useState(false);
    const [courseSearchQuery, setCourseSearchQuery] = useState('');
    const [tempSelectedCourses, setTempSelectedCourses] = useState(['all']); // Temporary selection in modal
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [coordinateCourses, setCoordinateCourses] = useState({});
    const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
    const [statusDeliveryModal, setStatusDeliveryModal] = useState(null); // { studentId, studentName, registrationId, courseLabel, registrationStatus, deliveryStatus }
    const [statusDeliverySaving, setStatusDeliverySaving] = useState(false);
    const [viewHistoryTarget, setViewHistoryTarget] = useState(null);
    const [addHistoryTarget, setAddHistoryTarget] = useState(null);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
    });

    const computedTotal = useMemo(() => pagination.total || students.length || 0, [pagination.total, students.length]);

    const derivedLastPage = useMemo(() => {
        const per = pagination.per_page || 1;
        const calculated = Math.ceil((computedTotal || 0) / per) || 1;
        return Math.max(1, pagination.last_page || calculated);
    }, [pagination, computedTotal]);

    const user = AuthService.getUser();
    const hasFullAccess = Boolean(
        user?.superAdmin?.superAdminId || user?.superAdmin?.staff?.type === 'admin'
    );
    const coordinateCourseIds = useMemo(() => {
        if (!Array.isArray(accessibleCourseIds) || accessibleCourseIds.length === 0) return [];
        return accessibleCourseIds
            .map((id) => {
                const num = Number(id);
                return Number.isFinite(num) ? num.toString() : null;
            })
            .filter((id) => id !== null);
    }, [accessibleCourseIds]);

    const canModifyRegistration = useCallback((reg) => {
        if (hasFullAccess) return true;
        if (!reg || reg.courseId == null) return false;
        const regCourseId = reg.courseId.toString();
        return coordinateCourseIds.includes(regCourseId);
    }, [coordinateCourseIds, hasFullAccess]);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
                if (!isMounted) return;

                const data = accessRes.data || {};
                setCoordinateCourses(data.coordinateCourses || {});
                setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
            } catch (e) {
                console.error('Failed to load coordinate courses for Students page', e);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        fetchStudents();
    }, [selectedCourses, deliveryStatus, registrationStatus, pagination.current_page, pagination.per_page, showMissingPhotosOnly]);

    useEffect(() => {
        fetchCourses();
    }, []);

    useEffect(() => {
        if (!hasFullAccess) {
            // Default to 'all' for coordinators, which means all their coordinated courses
            setSelectedCourses(['all']);
        }
    }, [hasFullAccess, coordinateCourseIds]);

    const handleDownloadTemplate = useCallback(() => {
        try {
            const worksheet = XLSX.utils.aoa_to_sheet([STUDENT_TEMPLATE_COLUMNS]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
            const timestamp = new Date().toISOString().split('T')[0];
            // Include University fields in the filename to make the purpose clear
            XLSX.writeFile(workbook, `Student_Import_Template_with_University_${timestamp}.xlsx`);
        } catch (error) {
            console.error('Template download failed:', error);
            Swal.fire('Download failed', 'Unable to create the template. Please try again.', 'error');
        }
    }, []);

    const handleDownloadUniversityTemplate = useCallback(() => {
        try {
            const headers = [
                'Student ID',
                'Registration Number',
                'University ID',
                'University Register Date (dd/mm/yyyy)'
            ];

            // Build rows from the currently loaded (filtered & paginated) students
            // Use the registration that matches the current course filter when possible
            const rows = (students || []).reduce((acc, student) => {
                const regs = Array.isArray(student.registrations) ? student.registrations : [];

                // Determine which registration is relevant based on the active course filter
                const hasSpecificCourseFilter = !selectedCourses.includes('all');
                let chosenReg = null;

                if (regs.length > 0) {
                    if (hasSpecificCourseFilter) {
                        const filterCourseIds = selectedCourses.map((id) => id.toString());
                        chosenReg = regs.find(
                            (r) => r && r.courseId != null && filterCourseIds.includes(r.courseId.toString())
                        ) || null;

                        // If there is a specific course filter and no matching registration,
                        // skip this student to avoid exporting an unrelated registration number.
                        if (!chosenReg) {
                            return acc;
                        }
                    } else if (!hasFullAccess && coordinateCourseIds.length > 0) {
                        const accessibleIds = coordinateCourseIds.map((id) => id.toString());
                        chosenReg = regs.find(
                            (r) => r && r.courseId != null && accessibleIds.includes(r.courseId.toString())
                        ) || regs[0];
                    } else {
                        // Admin with full access and no specific course filter – fallback to first registration
                        chosenReg = regs[0];
                    }
                } else {
                    chosenReg = {};
                }

                const studentId = student.studentId || '';
                const registrationNumberRaw = chosenReg.registrationNumber || '';
                const registrationNumber = registrationNumberRaw || '';
                const universityID = chosenReg.universityID || '';

                let universityRegisterDate = '';
                if (chosenReg.universityRegisterDate) {
                    const d = new Date(chosenReg.universityRegisterDate);
                    if (!Number.isNaN(d.getTime())) {
                        const dd = String(d.getDate()).padStart(2, '0');
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const yyyy = d.getFullYear();
                        universityRegisterDate = `${dd}/${mm}/${yyyy}`;
                    }
                }

                acc.push([
                    studentId,
                    registrationNumber,
                    universityID,
                    universityRegisterDate
                ]);

                return acc;
            }, []);

            if (!rows.length) {
                Swal.fire(
                    'No registrations found',
                    'There are no registrations in the current student list to include in the template.',
                    'info'
                );
                return;
            }

            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'University Registration');
            const timestamp = new Date().toISOString().split('T')[0];
            XLSX.writeFile(workbook, `University_Registration_Template_${timestamp}.xlsx`);
        } catch (error) {
            console.error('University template download failed:', error);
            Swal.fire('Download failed', 'Unable to create the university template. Please try again.', 'error');
        }
    }, [students, selectedCourses, hasFullAccess, coordinateCourseIds]);

    const handleDownloadPathwayTemplate = useCallback(() => {
        try {
            const headers = [
                'Student ID',
                'Student Name',
                'Registration Number',
                'Batch Name',
                'Batch WhatsApp Group',
                'Current Delivery Status',
                'Delivery Status',
                'Current Stream',
                'Stream'
            ];

            const hasSpecificCourseFilter = !selectedCourses.includes('all');
            const filterCourseIds = hasSpecificCourseFilter ? selectedCourses.map((id) => id.toString()) : [];
            const coordinatorCourseIdsAsString = !hasFullAccess && !hasSpecificCourseFilter && coordinateCourseIds.length > 0
                ? coordinateCourseIds.map((id) => id.toString())
                : [];

            const rows = (students || []).reduce((acc, student) => {
                const regs = Array.isArray(student.registrations) ? student.registrations : [];
                regs.forEach((reg) => {
                    const courseNameValue = typeof reg?.courseName === 'string' ? reg.courseName.toLowerCase() : '';
                    if (!courseNameValue.includes('hnd in computing')) {
                        return;
                    }

                    const regCourseId = reg && reg.courseId != null ? reg.courseId.toString() : '';

                    // Respect the current course filter when deciding which registrations to export
                    if (hasSpecificCourseFilter) {
                        if (!regCourseId || !filterCourseIds.includes(regCourseId)) {
                            return;
                        }
                    } else if (coordinatorCourseIdsAsString.length > 0) {
                        if (!regCourseId || !coordinatorCourseIdsAsString.includes(regCourseId)) {
                            return;
                        }
                    }

                    const studentId = student.studentId || '';
                    const studentName = student.fullName || '';
                    const registrationNumberRaw = reg.registrationNumber || '';
                    const registrationNumber = registrationNumberRaw || '';
                    const batchName = reg.batchName || reg.batch || '';
                    const whatsappName = reg.whatsappGroupName || reg.whatsappGroup || reg.whatsapp_group_id || '';
                    const deliveryStatusRaw = reg.deliveryStatus || '';
                    const streamRaw = reg.stream || '';

                    const currentDeliveryStatus = deliveryStatusRaw;
                    const currentStream = streamRaw;

                    const editableDeliveryStatus = deliveryStatusRaw || DELIVERY_STATUS_TEMPLATE_OPTIONS.join(', ');
                    const editableStream = streamRaw || STREAM_TEMPLATE_OPTIONS.join(', ');

                    acc.push([
                        studentId,
                        studentName,
                        registrationNumber,
                        batchName,
                        whatsappName,
                        currentDeliveryStatus,
                        editableDeliveryStatus,
                        currentStream,
                        editableStream
                    ]);
                });

                return acc;
            }, []);

            if (!rows.length) {
                Swal.fire(
                    'No pathway records',
                    'Only HND in Computing registrations can have a stream. No matching records were found in the current student list.',
                    'info'
                );
                return;
            }

            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Pathway Stream');

            const validationSheetData = [];
            validationSheetData.push(['Delivery Status', 'Stream']);
            const maxRows = Math.max(DELIVERY_STATUS_TEMPLATE_OPTIONS.length, STREAM_TEMPLATE_OPTIONS.length);
            for (let i = 0; i < maxRows; i += 1) {
                validationSheetData.push([
                    DELIVERY_STATUS_TEMPLATE_OPTIONS[i] || '',
                    STREAM_TEMPLATE_OPTIONS[i] || ''
                ]);
            }
            const validationSheet = XLSX.utils.aoa_to_sheet(validationSheetData);
            XLSX.utils.book_append_sheet(workbook, validationSheet, 'ValidationLists');

            workbook.Workbook = workbook.Workbook || {};
            workbook.Workbook.Sheets = workbook.Workbook.Sheets || [];
            while (workbook.Workbook.Sheets.length < workbook.SheetNames.length) {
                workbook.Workbook.Sheets.push({});
            }
            const validationIndex = workbook.SheetNames.indexOf('ValidationLists');
            if (validationIndex >= 0) {
                workbook.Workbook.Sheets[validationIndex] = { Hidden: 1 };
            }

            const totalRows = rows.length + 1;
            const deliveryRangeEnd = DELIVERY_STATUS_TEMPLATE_OPTIONS.length + 1;
            const streamRangeEnd = STREAM_TEMPLATE_OPTIONS.length + 1;
            worksheet['!dataValidation'] = [
                {
                    sqref: `F2:F${totalRows}`,
                    type: 'list',
                    allowBlank: true,
                    formulas: [`=ValidationLists!$A$2:$A$${deliveryRangeEnd}`]
                },
                {
                    sqref: `G2:G${totalRows}`,
                    type: 'list',
                    allowBlank: true,
                    formulas: [`=ValidationLists!$B$2:$B$${streamRangeEnd}`]
                }
            ];
            const timestamp = new Date().toISOString().split('T')[0];
            XLSX.writeFile(workbook, `Pathway_Stream_Template_${timestamp}.xlsx`);
        } catch (error) {
            console.error('Pathway template download failed:', error);
            Swal.fire('Download failed', 'Unable to create the pathway verification template. Please try again.', 'error');
        }
    }, [students, selectedCourses, hasFullAccess, coordinateCourseIds]);

    const handleDownloadGoogleContacts = useCallback(() => {
        try {
            const headers = [
                'Name Prefix',
                'First Name',
                'Middle Name',
                'Last Name',
                'Name Suffix',
                'Phonetic First Name',
                'Phonetic Middle Name',
                'Phonetic Last Name',
                'Nickname',
                'File As',
                'E-mail 1 - Label',
                'E-mail 1 - Value',
                'Phone 1 - Label',
                'Phone 1 - Value',
                'Address 1 - Label',
                'Address 1 - Country',
                'Address 1 - Street',
                'Address 1 - Extended Address',
                'Address 1 - City',
                'Address 1 - Region',
                'Address 1 - Postal Code',
                'Address 1 - PO Box',
                'Organization Name',
                'Organization Title',
                'Organization Department',
                'Birthday',
                'Event 1 - Label',
                'Event 1 - Value',
                'Relation 1 - Label',
                'Relation 1 - Value',
                'Website 1 - Label',
                'Website 1 - Value',
                'Custom Field 1 - Label',
                'Custom Field 1 - Value',
                'Notes',
                'Labels'
            ];

            // Build rows from the currently loaded students
            const rows = (students || []).map((student) => {
                const fullName = student.fullName || '';
                const studentId = student.studentId || '';
                
                // Split full name into parts (assuming format: "First Middle Last")
                const nameParts = fullName.trim().split(/\s+/);
                const firstName = nameParts[0] || '';
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
                const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';

                // Format: "Student ID - Full Name"
                const fileAs = `${studentId} - ${fullName}`;
                
                const email = student.email || '';
                const telephone = student.telephone || '';
                const address = student.address || '';
                
                // Format date of birth if available
                let birthday = '';
                if (student.dob) {
                    const d = new Date(student.dob);
                    if (!Number.isNaN(d.getTime())) {
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        const yyyy = d.getFullYear();
                        birthday = `${yyyy}-${mm}-${dd}`;
                    }
                }

                return [
                    '', // Name Prefix
                    firstName, // First Name
                    middleName, // Middle Name
                    lastName, // Last Name
                    '', // Name Suffix
                    '', // Phonetic First Name
                    '', // Phonetic Middle Name
                    '', // Phonetic Last Name
                    '', // Nickname
                    fileAs, // File As - "Student ID - Full Name"
                    'Work', // E-mail 1 - Label
                    email, // E-mail 1 - Value
                    'Mobile', // Phone 1 - Label
                    telephone, // Phone 1 - Value
                    'Home', // Address 1 - Label
                    '', // Address 1 - Country
                    address, // Address 1 - Street
                    '', // Address 1 - Extended Address
                    '', // Address 1 - City
                    '', // Address 1 - Region
                    '', // Address 1 - Postal Code
                    '', // Address 1 - PO Box
                    'ESOFT', // Organization Name
                    'Student', // Organization Title
                    '', // Organization Department
                    birthday, // Birthday
                    '', // Event 1 - Label
                    '', // Event 1 - Value
                    '', // Relation 1 - Label
                    '', // Relation 1 - Value
                    '', // Website 1 - Label
                    '', // Website 1 - Value
                    'Student ID', // Custom Field 1 - Label
                    studentId, // Custom Field 1 - Value
                    '', // Notes
                    'Students' // Labels
                ];
            });

            if (!rows.length) {
                Swal.fire(
                    'No students found',
                    'There are no students in the current list to export.',
                    'info'
                );
                return;
            }

            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            
            // Create and download CSV file
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            const timestamp = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `Google_Contacts_Students_${timestamp}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            Swal.fire({
                icon: 'success',
                title: 'Export Successful',
                text: `Exported ${rows.length} student(s) to Google Contacts CSV format`,
                confirmButtonColor: '#64748b',
            });
        } catch (error) {
            console.error('Google Contacts export failed:', error);
            Swal.fire('Export failed', 'Unable to create the Google Contacts file. Please try again.', 'error');
        }
    }, [students]);

    const handleOpenUniversityUploadPage = useCallback(() => {
        window.open('/admin/students/university-registration-upload', '_blank', 'noopener,noreferrer');
    }, []);

    const handleOpenPathwayUploadPage = useCallback(() => {
        window.open('/admin/students/pathway-stream-upload', '_blank', 'noopener,noreferrer');
    }, []);

    const handleOpenUploadPage = useCallback(() => {
        window.open('/admin/students/upload', '_blank', 'noopener,noreferrer');
    }, []);

    const fetchStudents = async (customSearch = null) => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current_page,
                perPage: pagination.per_page,
                deliveryStatus: deliveryStatus,
                registrationStatus: registrationStatus
            };

            if (showMissingPhotosOnly) {
                params.hasImage = 'false';
            }

            // Build courseFilter from selectedCourses array
            if (selectedCourses.includes('all')) {
                // For admins: 'all' means truly all courses
                // For coordinators: 'all' means all their coordinated courses
                params.courseFilter = hasFullAccess ? 'all' : coordinateCourseIds.join(',');
            } else {
                params.courseFilter = selectedCourses.join(',');
            }
            
            // Use customSearch if provided, otherwise use searchTerm state
            const searchValue = customSearch !== null ? customSearch : searchTerm;
            if (searchValue) {
                params.search = searchValue;
            }
            
            const response = await axiosInstance.get('/admin/students', { params });

            console.log('API Response:', {
                total: response.data.total,
                pagination: response.data.pagination,
                studentsLength: response.data.students?.length,
                params: params
            });

            const apiPagination = response.data.pagination || {};
            const apiTotal = response.data.total;
            const total = Number(apiTotal ?? apiPagination.total ?? 0) || (response.data.students?.length || 0);
            const perPage = Number(apiPagination.per_page ?? pagination.per_page ?? 20) || 20;
            const calculatedLastPage = Math.max(1, Math.ceil(total / perPage));
            const currentPage = Math.min(
                Math.max(1, Number(apiPagination.current_page ?? pagination.current_page ?? 1)),
                calculatedLastPage
            );

            console.log('Computed Pagination:', {
                total,
                perPage,
                calculatedLastPage,
                currentPage
            });

            setStudents(response.data.students || []);
            setPagination({
                current_page: currentPage,
                last_page: Number(apiPagination.last_page ?? calculatedLastPage) || calculatedLastPage,
                per_page: perPage,
                total: total,
            });
            setError(null);
        } catch (err) {
            console.error('Error fetching students:', err);
            setError('No students match the selected filters. Please adjust filters and try again.');
            setStudents([]);
            setPagination((prev) => ({ ...prev, total: 0, current_page: 1, last_page: 1 }));
        } finally {
            setLoading(false);
        }
    };

    const fetchCourses = async () => {
        try {
            const accessibleHeader = hasFullAccess ? 'all' : coordinateCourseIds.join(',');
            const roleHeader = hasFullAccess ? 'admin' : 'staff';
            const response = await axiosInstance.get('/admin/courses', {
                headers: {
                    'x-accessible-courses': accessibleHeader,
                    'x-role': roleHeader,
                },
            });
            const primaryCourses = response.data.courses || [];
            if (primaryCourses.length > 0) {
                setCourses(primaryCourses);
                return;
            }
            // Fallback to dashboard payload if the courses endpoint returns empty
            const dashResp = await axiosInstance.get('/admin/dashboard');
            setCourses(dashResp?.data?.courses || []);
        } catch (err) {
            console.error('Error fetching courses:', err);
            try {
                const accessibleHeader = hasFullAccess ? 'all' : coordinateCourseIds.join(',');
                const roleHeader = hasFullAccess ? 'admin' : 'staff';
                const dashResp = await axiosInstance.get('/admin/dashboard', {
                    headers: {
                        'x-accessible-courses': accessibleHeader,
                        'x-role': roleHeader,
                    },
                });
                setCourses(dashResp?.data?.courses || []);
            } catch (e) {
                console.error('Fallback dashboard fetch failed:', e);
            }
        }
    };

    const handleSearch = () => {
        const term = searchInput.trim();
        setSearchTerm(term);
        setPagination(prev => ({ ...prev, current_page: 1 }));
        // Pass the search term directly to avoid state update delay
        fetchStudents(term);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setPagination(prev => ({ ...prev, current_page: 1 }));
        // Pass empty string directly to clear search immediately
        fetchStudents('');
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    
    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            current_page: Math.max(1, Math.min(newPage, derivedLastPage)),
        }));
    };

    const handlePerPageChange = (e) => {
        const value = Math.min(200, Number(e.target.value) || 20);
        setPagination(prev => ({ ...prev, per_page: value, current_page: 1 }));
    };

    const handleOpenCourseFilterModal = () => {
        setTempSelectedCourses([...selectedCourses]);
        setCourseFilterModal(true);
    };

    const handleToggleCourse = (courseId) => {
        if (courseId === 'all') {
            setTempSelectedCourses(['all']);
        } else {
            const newSelection = tempSelectedCourses.filter(id => id !== 'all');
            if (newSelection.includes(courseId)) {
                const filtered = newSelection.filter(id => id !== courseId);
                setTempSelectedCourses(filtered.length > 0 ? filtered : ['all']);
            } else {
                setTempSelectedCourses([...newSelection, courseId]);
            }
        }
    };

    const handleApplyCourseFilter = () => {
        setSelectedCourses([...tempSelectedCourses]);
        setPagination(prev => ({ ...prev, current_page: 1 }));
        setCourseFilterModal(false);
    };

    const handleCancelCourseFilter = () => {
        setCourseFilterModal(false);
        setCourseSearchQuery('');
    };
    
    const handleDeliveryStatusChange = (status) => {
        setDeliveryStatus(status);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handleRegistrationStatusChange = (status) => {
        setRegistrationStatus(status);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handleMissingPhotoToggle = () => {
        setShowMissingPhotosOnly((prev) => {
            const next = !prev;
            setPagination((current) => ({ ...current, current_page: 1 }));
            return next;
        });
    };

    // Filter students based on search and course
    const visibleCourses = useMemo(() => {
        if (hasFullAccess || coordinateCourseIds.length === 0) return courses;
        return courses.filter((course) => coordinateCourseIds.includes(course.courseId?.toString()));
    }, [courses, hasFullAccess, coordinateCourseIds]);

    const filteredStudents = useMemo(() => {
        // Server-side filtering via API, so just return students
        return students;
    }, [students]);

    // Calculate Registration Status counts from current page
    const registrationStatusCounts = useMemo(() => {
        const counts = {
            active: 0,
            postponed: 0,
            pending: 0,
            suspending: 0,
            inactivating: 0,
            inactivated: 0,
            suspended: 0
        };
        
        filteredStudents.forEach(student => {
            if (student.registrations && Array.isArray(student.registrations)) {
                student.registrations.forEach(reg => {
                    const status = reg.registrationStatus?.toLowerCase();
                    if (status && counts.hasOwnProperty(status)) {
                        counts[status]++;
                    }
                });
            }
        });
        
        return counts;
    }, [filteredStudents]);

    // Calculate Delivery Status counts from current page
    const deliveryStatusCounts = useMemo(() => {
        const counts = {
            active: 0,
            'academic delivery completed': 0,
            graduated: 0,
            'certificate received': 0,
            'certificate dispatched': 0,
            dropped: 0,
            'transfer to another branch': 0,
            'transfer to another course': 0
        };
        
        filteredStudents.forEach(student => {
            if (student.registrations && Array.isArray(student.registrations)) {
                student.registrations.forEach(reg => {
                    const status = reg.deliveryStatus?.toLowerCase() || 'active';
                    if (counts.hasOwnProperty(status)) {
                        counts[status]++;
                    } else if (status === '' || !reg.deliveryStatus) {
                        counts.active++;
                    }
                });
            }
        });
        
        return counts;
    }, [filteredStudents]);

    const currentStudentIndex = useMemo(() => {
        if (!selectedStudent) return -1;
        return filteredStudents.findIndex((s) => s.studentId === selectedStudent.studentId);
    }, [filteredStudents, selectedStudent]);

    const hasPrevImage = currentStudentIndex > 0;
    const hasNextImage = currentStudentIndex >= 0 && currentStudentIndex < filteredStudents.length - 1;

    const showPrevImage = () => {
        if (hasPrevImage) {
            setSelectedStudent(filteredStudents[currentStudentIndex - 1]);
        }
    };

    const showNextImage = () => {
        if (hasNextImage) {
            setSelectedStudent(filteredStudents[currentStudentIndex + 1]);
        }
    };

    const getSelectionSummary = () => {
        if (selectedCourses.includes('all')) {
            return hasFullAccess ? 'All Courses Selected' : 'All Accessible Courses Selected';
        }
        if (selectedCourses.length === 1) {
            const course = visibleCourses.find(c => c.courseId.toString() === selectedCourses[0]);
            return course?.fullName || `${selectedCourses.length} Course Selected`;
        }
        return `${selectedCourses.length} Courses Selected`;
    };

    const getFilteredCourseOptions = () => {
        if (!courseSearchQuery) return visibleCourses;
        const query = courseSearchQuery.toLowerCase();
        return visibleCourses.filter(course => {
            const schedule = course.scheduleType ? String(course.scheduleType).toLowerCase() : '';
            return (
                course.fullName?.toLowerCase().includes(query) ||
                course.shortName?.toLowerCase().includes(query) ||
                schedule.includes(query)
            );
        });
    };

    const getInitials = (name) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
    };

    const parseLocalDate = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        if (!trimmed) return null;
        // Handle plain date strings (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [y, m, d] = trimmed.split('-').map(Number);
            if ([y, m, d].some((n) => Number.isNaN(n))) return null;
            return new Date(y, m - 1, d);
        }
        // Handle ISO date-time strings by ignoring the time + timezone part
        // to avoid off-by-one issues when converted to the local timezone.
        if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
            const [datePart] = trimmed.split('T');
            const [y, m, d] = datePart.split('-').map(Number);
            if ([y, m, d].some((n) => Number.isNaN(n))) return null;
            return new Date(y, m - 1, d);
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDate = (dateString) => {
        const parsed = parseLocalDate(dateString);
        if (!parsed) return 'N/A';
        return parsed.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const formatUniversityRegisterInfo = (dateString) => {
        const parsed = parseLocalDate(dateString);
        if (!parsed) return null;

        const formattedDate = parsed.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });

        const now = new Date();
        let months = (now.getFullYear() - parsed.getFullYear()) * 12 + (now.getMonth() - parsed.getMonth());

        if (now.getDate() < parsed.getDate()) {
            months -= 1;
        }
        if (months < 0) months = 0;

        const monthsLabel = months === 1 ? '1 month' : `${months} months`;

        return { formattedDate, monthsLabel, monthsCount: months };
    };


    const openStatusDeliveryModal = (student, reg) => {
        if (!reg || !reg.registrationId) {
            return;
        }

        // Only allow updates if user has permission over this course
        if (!canModifyRegistration(reg)) {
            Swal.fire({
                icon: 'info',
                title: 'Not allowed',
                text: 'You can update status and delivery only for courses you coordinate.',
                confirmButtonColor: '#64748b',
            });
            return;
        }

        const batchName = (reg.batchName || reg.batch || '').trim();
        const courseLabel = `${reg.courseName || 'Course'}${batchName ? ` (${batchName})` : ''}`;

        setStatusDeliveryModal({
            studentId: student.studentId,
            studentName: student.fullName || '',
            registrationId: reg.registrationId,
            courseLabel,
            registrationStatus: reg.registrationStatus || 'active',
            deliveryStatus: reg.deliveryStatus || '',
        });
    };

    const handleOpenViewHistory = (student, reg) => {
        if (!reg || !reg.registrationId) {
            Swal.fire({
                icon: 'info',
                title: 'Registration required',
                text: 'History is available only after the student is registered for a course.',
                confirmButtonColor: '#64748b',
            });
            return;
        }

        // Only coordinators (or full-access users) can view history
        if (!canModifyRegistration(reg)) {
            Swal.fire({
                icon: 'info',
                title: 'Not allowed',
                text: 'You can view history only for courses you coordinate.',
                confirmButtonColor: '#64748b',
            });
            return;
        }

        setViewHistoryTarget({ student, registration: reg });
    };

    const handleOpenAddHistory = (student, reg) => {
        if (!reg || !reg.registrationId) {
            Swal.fire({
                icon: 'info',
                title: 'Registration required',
                text: 'Please register the student before adding history.',
                confirmButtonColor: '#64748b',
            });
            return;
        }

        if (!canModifyRegistration(reg)) {
            Swal.fire({
                icon: 'info',
                title: 'Not allowed',
                text: 'You can add history only for courses you coordinate.',
                confirmButtonColor: '#64748b',
            });
            return;
        }

        setAddHistoryTarget({ student, registration: reg });
    };

    const handleHistoryAdded = (registrationId) => {
        if (!registrationId) return;
        fetchStudents();
        if (viewHistoryTarget?.registration?.registrationId === registrationId) {
            setHistoryRefreshKey((prev) => prev + 1);
        }
    };

    const closeStatusDeliveryModal = () => {
        if (statusDeliverySaving) return;
        setStatusDeliveryModal(null);
    };

    const handleStatusDeliverySave = async () => {
        if (!statusDeliveryModal || statusDeliverySaving) return;

        const { registrationId, studentId, registrationStatus, deliveryStatus } = statusDeliveryModal;

        try {
            setStatusDeliverySaving(true);

            const payload = {};
            if (typeof registrationStatus === 'string' && registrationStatus.trim() !== '') {
                payload.registrationStatus = registrationStatus.trim();
            }
            if (typeof deliveryStatus === 'string') {
                payload.deliveryStatus = deliveryStatus;
            }

            const { data } = await axiosInstance.put(`/admin/registrations/${registrationId}`, payload);

            const updated = data && data.registration ? data.registration : null;
            const nextRegStatus = updated && updated.registrationStatus ? updated.registrationStatus : registrationStatus;
            const nextDelivStatus = updated && Object.prototype.hasOwnProperty.call(updated, 'deliveryStatus')
                ? updated.deliveryStatus
                : deliveryStatus;

            setStudents((prev) =>
                prev.map((stu) => {
                    if (stu.studentId !== studentId) return stu;
                    return {
                        ...stu,
                        registrations: (stu.registrations || []).map((r) => {
                            if (r.registrationId !== registrationId) return r;
                            return {
                                ...r,
                                registrationStatus: nextRegStatus,
                                deliveryStatus: nextDelivStatus,
                            };
                        }),
                    };
                })
            );

            await Swal.fire({
                icon: 'success',
                title: 'Updated',
                text: 'Registration status and delivery have been updated.',
                confirmButtonColor: '#64748b',
            });

            setStatusDeliveryModal(null);
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Update failed',
                text: err.response?.data?.message || 'Failed to update registration',
                confirmButtonColor: '#64748b',
            });
        } finally {
            setStatusDeliverySaving(false);
        }
    };

    const closeModal = () => {
        setActiveModal(null);
        setSelectedStudent(null);
    };

    return (
        <DashboardLayout title="Students">
            {/* Header */}
            <div className="mb-6 space-y-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">All Students</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        View and manage all student information
                    </p>
                </div>
                <div className="flex items-center justify-end">
                    <div className="flex items-center gap-3">
                    {hasFullAccess && (
                        <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
                            <span className="hidden text-xs font-medium text-slate-500 lg:inline">University</span>
                            <button
                                onClick={handleOpenUniversityUploadPage}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 transition hover:bg-slate-50"
                                aria-label="Upload university registration template"
                                title="Upload university registration template"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0-4-4m4 4 4-4" />
                                </svg>
                            </button>
                            <button
                                onClick={handleDownloadUniversityTemplate}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 transition hover:bg-slate-50"
                                aria-label="Download university ID template"
                                title="Download university ID template"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v14m0 0l-4-4m4 4 4-4M4 21h16" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {hasFullAccess && (
                        <div className="flex items-center gap-1 rounded-xl border border-fuchsia-200 bg-fuchsia-50 px-2 py-1 shadow-sm">
                            <span className="hidden text-xs font-medium text-fuchsia-700 lg:inline">Pathway</span>
                            <button
                                onClick={handleOpenPathwayUploadPage}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-fuchsia-700 transition hover:bg-fuchsia-100"
                                aria-label="Upload pathway stream template"
                                title="Upload pathway stream template"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0-4-4m4 4 4-4" />
                                </svg>
                            </button>
                            <button
                                onClick={handleDownloadPathwayTemplate}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-fuchsia-700 transition hover:bg-fuchsia-100"
                                aria-label="Download pathway stream template"
                                title="Download pathway stream template"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v14m0 0l-4-4m4 4 4-4M4 21h16" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {hasFullAccess && (
                        <div className="flex items-center gap-1 rounded-xl border border-teal-200 bg-teal-50 px-2 py-1 shadow-sm">
                            <span className="hidden text-xs font-medium text-teal-700 lg:inline">Contacts</span>
                            <button
                                onClick={handleDownloadGoogleContacts}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-teal-700 transition hover:bg-teal-100"
                                aria-label="Download Google Contacts"
                                title="Download Google Contacts"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {hasFullAccess ? (
                        <div className="flex items-center gap-1 rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-1 shadow-sm">
                            <span className="hidden text-xs font-medium text-emerald-700 lg:inline">Students</span>
                            <button
                                onClick={handleOpenUploadPage}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-emerald-800 transition hover:bg-emerald-100"
                                aria-label="Upload student list"
                                title="Upload student list"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0-4-4m4 4 4-4" />
                                </svg>
                            </button>
                            <button
                                onClick={handleDownloadTemplate}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-indigo-700 transition hover:bg-indigo-50"
                                aria-label="Download student template"
                                title="Download student template"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0 4-4m-4 4-4-4" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4h6" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleDownloadTemplate}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-indigo-200 bg-white text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                            aria-label="Download student template"
                            title="Download student template"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v10m0 0 4-4m-4 4-4-4" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4h6" />
                            </svg>
                        </button>
                    )}
                    <button
                        onClick={() => setShowSummaryModal(true)}
                        className="flex items-center gap-2 rounded-lg border-2 border-blue-600 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100"
                        title="View Summary Statistics"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="hidden sm:inline">Summary</span>
                    </button>
                    <button
                        onClick={() => {
                            setSelectedStudent(null);
                            setActiveModal('add-student');
                        }}
                        className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Student
                    </button>
                    </div>
                </div>

                
            </div>

            {/* Statistics Summary */}
            <div className="mb-6 space-y-4">
                {/* Main Stats */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 p-6 shadow-sm border border-indigo-100">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-indigo-500 p-3 text-white">
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-600">Total Students (all pages)</p>
                                <p className="mt-1 text-3xl font-bold text-indigo-600">{pagination.total || students.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-6 shadow-sm border border-emerald-100">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-emerald-500 p-3 text-white">
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-600">Students on this page</p>
                                <p className="mt-1 text-3xl font-bold text-emerald-600">{filteredStudents.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-6 shadow-sm border border-blue-100">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-blue-500 p-3 text-white">
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-600">Available Courses</p>
                                <p className="mt-1 text-3xl font-bold text-blue-600">{visibleCourses.length}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-6 space-y-4">
                {/* Search Bar */}
                <div className="flex flex-wrap gap-4">
                    <div className="flex flex-1 min-w-[300px] gap-2">
                        <input
                            type="text"
                            placeholder="Search by name, student ID, registration number, email, NIC, mobile, batch name, WhatsApp group..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleSearch}
                            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Find
                        </button>
                        {searchTerm && (
                            <button
                                onClick={handleClearSearch}
                                className="flex items-center gap-2 rounded-lg bg-gray-500 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid gap-3 grid-cols-1 md:grid-cols-4">
                    {/* Registration Status Filter */}
                    <div className="rounded-lg border border-teal-200 bg-white p-3 shadow-sm">
                        <label className="block text-xs font-medium text-gray-600 mb-2">Registration Status</label>
                        <Select
                            value={{ value: registrationStatus, label: registrationStatus === 'active' ? 'Active' : registrationStatus === 'all' ? 'All Statuses' : registrationStatus }}
                            onChange={(option) => handleRegistrationStatusChange(option?.value || 'all')}
                            options={[
                                { value: 'all', label: 'All Statuses' },
                                ...REGISTRATION_STATUS_OPTIONS,
                            ]}
                            isSearchable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '0.5rem',
                                    borderWidth: '1px',
                                    borderColor: '#99f6e4',
                                    fontSize: '0.875rem',
                                    minHeight: '38px',
                                    boxShadow: 'none',
                                    '&:hover': { borderColor: '#14b8a6' },
                                }),
                                dropdownIndicator: (base) => ({ ...base, color: '#0f766e', padding: '6px' }),
                                indicatorSeparator: () => ({ display: 'none' }),
                            }}
                        />
                    </div>

                    {/* Delivery Status Filter */}
                    <div className="rounded-lg border border-purple-200 bg-white p-3 shadow-sm">
                        <label className="block text-xs font-medium text-gray-600 mb-2">Delivery Status</label>
                        <Select
                            value={{ value: deliveryStatus, label: deliveryStatus === 'active' ? 'Active (Ongoing)' : deliveryStatus === 'all' ? 'All Statuses' : deliveryStatus }}
                            onChange={(option) => handleDeliveryStatusChange(option?.value || 'all')}
                            options={[
                                { value: 'all', label: 'All Statuses' },
                                { value: 'active', label: 'Active (Ongoing)' },
                                { value: 'academic delivery completed', label: 'Academic Delivery Completed' },
                                { value: 'graduated', label: 'Graduated' },
                                { value: 'certificate received', label: 'Certificate Received' },
                                { value: 'certificate dispatched', label: 'Certificate Dispatched' },
                                { value: 'Dropped', label: 'Dropped' },
                                { value: 'Transfer to another branch', label: 'Transfer to another branch' },
                                { value: 'Transfer to another course', label: 'Transfer to another course' }
                            ]}
                            isSearchable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '0.5rem',
                                    borderWidth: '1px',
                                    borderColor: '#e9d5ff',
                                    fontSize: '0.875rem',
                                    minHeight: '38px',
                                    boxShadow: 'none',
                                    '&:hover': { borderColor: '#c084fc' }
                                }),
                                dropdownIndicator: (base) => ({ ...base, color: '#7c3aed', padding: '6px' }),
                                indicatorSeparator: () => ({ display: 'none' }),
                            }}
                        />
                    </div>

                    {/* Course Filter */}
                    <div className="rounded-lg border border-indigo-200 bg-white p-3 shadow-sm">
                        <label className="block text-xs font-medium text-gray-600 mb-2">Filter by Course</label>
                        <button
                            onClick={handleOpenCourseFilterModal}
                            className="flex w-full items-center justify-between rounded-lg border border-indigo-200 bg-white px-3 py-2 text-left text-sm text-gray-800 transition hover:border-indigo-400 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 h-[38px]"
                        >
                            <span className="truncate text-sm">{getSelectionSummary()}</span>
                            <svg className="h-4 w-4 text-indigo-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>

                    {/* Photo Filter */}
                    <div className="rounded-lg border border-amber-200 bg-white p-3 shadow-sm">
                        <label className="block text-xs font-medium text-gray-600 mb-2">Photo Filter</label>
                        <button
                            type="button"
                            onClick={handleMissingPhotoToggle}
                            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition h-[38px] ${
                                showMissingPhotosOnly 
                                    ? 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100' 
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <span>{showMissingPhotosOnly ? 'Missing photos only' : 'All students'}</span>
                            <div className={`h-5 w-9 rounded-full transition-colors ${showMissingPhotosOnly ? 'bg-amber-500' : 'bg-gray-300'} relative flex-shrink-0`}>
                                <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${showMissingPhotosOnly ? 'translate-x-4' : 'translate-x-0'}`}></span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4 text-red-800">
                    <div className="flex items-center gap-2">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{error}</span>
                    </div>
                </div>
            )}

            {/* Students Cards */}
            <div className="space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center p-12">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600"></div>
                            <p className="text-sm font-medium text-gray-600">Loading students...</p>
                        </div>
                    </div>
                ) : filteredStudents.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600">No students found</p>
                    </div>
                ) : (
                    filteredStudents.map((student) => {
                        const isWithinCoordination = hasFullAccess || (
                            Array.isArray(student.registrations) &&
                            student.registrations.some((reg) =>
                                reg.courseId != null && coordinateCourseIds.includes(reg.courseId.toString())
                            )
                        );
                        return (
                        <div key={student.studentId} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div className="grid gap-4 md:grid-cols-[220px_1fr_140px] md:items-start">
                                {/* Left: Avatar + Reset Passwords */}
                                <div className="flex flex-col items-center gap-3 rounded-lg bg-gradient-to-b from-white to-gray-50 p-3 shadow-sm">
                                    <div className="flex items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 p-1">
                                        {student.hasImage ? (
                                            <StudentPassportPhoto
                                                student={student}
                                                onClick={() => { setSelectedStudent(student); setActiveModal('view-image'); }}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => { setSelectedStudent(student); setActiveModal('upload-image'); }}
                                                className="flex h-28 w-20 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 text-2xl font-bold text-white hover:opacity-90"
                                                title="Click to upload photo"
                                            >
                                                {getInitials(student.fullName)}
                                            </button>
                                        )}
                                    </div>
                                    {isWithinCoordination && (
                                        <button
                                            onClick={() => { setSelectedStudent(student); setActiveModal('reset password'); }}
                                            className="w-full inline-flex items-center justify-center gap-1.5 rounded border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors"
                                        >
                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                            </svg>
                                            Reset Password
                                        </button>
                                    )}
                                    {isWithinCoordination && (
                                        <button
                                            onClick={() => { setSelectedStudent(student); setActiveModal('reset parent password'); }}
                                            className="w-full mt-1 inline-flex items-center justify-center gap-1.5 rounded border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100 transition-colors"
                                        >
                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                            </svg>
                                            Reset Parent Password
                                        </button>
                                    )}
                                </div>

                                {/* Middle: Details */}
                                <div className="border-y border-gray-100 pb-3 pt-1">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Student ID:</span><span className="text-gray-900">{student.studentId}</span></div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Full Name:</span><span className="text-gray-900">{student.fullName}</span></div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Email:</span><span className="text-gray-900">{student.email || 'N/A'}</span></div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Mobile:</span><span className="text-gray-900">N/A</span></div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Telephone:</span><span className="text-gray-900">{student.telephone || 'N/A'}</span></div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">Date of Birth:</span><span className="text-gray-900">{formatDate(student.dob)}</span></div>
                                        <div className="text-sm flex gap-2 items-center"><span className="w-28 font-medium text-gray-600">Gender:</span>{student.gender ? (<span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">{student.gender}</span>) : (<span className="text-gray-900">N/A</span>)}</div>
                                        <div className="text-sm flex gap-2"><span className="w-28 font-medium text-gray-600">NIC Number:</span><span className="text-gray-900">{student.nicNumber || 'N/A'}</span></div>
                                        <div className="text-sm sm:col-span-2 flex gap-2"><span className="w-28 font-medium text-gray-600">Address:</span><span className="text-gray-900">{student.address || 'N/A'}</span></div>
                                    </div>

                                    {/* Registrations */}
                                    <div className="mt-3">
                                        <div className="text-sm font-medium text-gray-700">Registered Courses:</div>
                                        <div className="mt-1 flex flex-wrap items-center gap-3">
                                            {student.registrations && student.registrations.length > 0 ? (
                                                student.registrations.map((reg, idx) => {
                                                    const batchName = (reg.batchName || reg.batch || '').trim();
                                                    const whatsappName = (reg.whatsappGroupName || reg.whatsappGroup || reg.whatsapp_group_id || '').trim();
                                                    const universityRegisterInfo = formatUniversityRegisterInfo(reg.universityRegisterDate);

                                                    return (
                                                        <div key={idx} className="flex flex-col gap-0.5">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="inline-flex rounded-full bg-purple-600 px-3 py-1 text-xs font-semibold text-white">
                                                                    {reg.courseName || 'N/A'}{batchName && ` (${batchName})`}
                                                                </span>
                                                            </div>
                                                            {reg.registrationNumber && (
                                                                <div className="flex flex-wrap items-center gap-1 text-xs text-gray-700">
                                                                    <div className="flex items-center gap-1">
                                                                        <svg
                                                                            className="h-3.5 w-3.5"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                        >
                                                                            <rect x="4" y="4" width="16" height="16" rx="2" ry="2" strokeWidth="2" />
                                                                            <path d="M8 9h8" strokeWidth="2" strokeLinecap="round" />
                                                                            <path d="M8 13h5" strokeWidth="2" strokeLinecap="round" />
                                                                        </svg>
                                                                        <span>Reg #: {reg.registrationNumber}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenViewHistory(student, reg)}
                                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-[10px] text-slate-500 hover:border-purple-400 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-40"
                                                                            title="View history"
                                                                            aria-label="View history"
                                                                            disabled={!reg.registrationId}
                                                                        >
                                                                            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {reg.registrationStatus && (
                                                                <div
                                                                    className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer"
                                                                    onDoubleClick={() => openStatusDeliveryModal(student, reg)}
                                                                    title="Double-click to update status and delivery"
                                                                >
                                                                    <svg
                                                                        className="h-3.5 w-3.5"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                    >
                                                                        <circle cx="12" cy="12" r="9" strokeWidth="2" />
                                                                        <path
                                                                            d="M9 12l2 2 4-4"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                    <span className="mr-1">Status:</span>
                                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${getRegistrationStatusColor(reg.registrationStatus)}`}>
                                                                        {reg.registrationStatus}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div
                                                                className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer"
                                                                onDoubleClick={() => openStatusDeliveryModal(student, reg)}
                                                                title="Double-click to update status and delivery"
                                                            >
                                                                <svg
                                                                    className="h-3.5 w-3.5"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        d="M3 7h18l-1.5 8.5a2 2 0 0 1-2 1.5H6.5a2 2 0 0 1-2-1.5L3 7z"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                    <path d="M8 18.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" strokeWidth="2" />
                                                                    <path d="M16 18.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" strokeWidth="2" />
                                                                </svg>
                                                                <span className="mr-1">Delivery:</span>
                                                                <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-700">
                                                                    {reg.deliveryStatus ? reg.deliveryStatus : 'Ongoing'}
                                                                </span>
                                                            </div>
                                                            {(reg.universityID || reg.universityRegisterDate) && (
                                                                <div className="flex items-center gap-1 text-xs text-gray-700 mt-0.5">
                                                                    <svg
                                                                        className="h-3.5 w-3.5"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                    >
                                                                        <rect x="3" y="4" width="18" height="14" rx="2" ry="2" strokeWidth="2" />
                                                                        <path d="M7 9h10" strokeWidth="2" strokeLinecap="round" />
                                                                        <path d="M7 13h6" strokeWidth="2" strokeLinecap="round" />
                                                                    </svg>
                                                                    <div className="flex flex-wrap items-center gap-1">
                                                                        {reg.universityID && (
                                                                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                                                                Uni ID: {reg.universityID}
                                                                            </span>
                                                                        )}
                                                                        {universityRegisterInfo && (
                                                                            <>
                                                                                <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                                                                    Uni reg: {universityRegisterInfo.formattedDate}  b7 {universityRegisterInfo.monthsLabel}
                                                                                </span>
                                                                                {universityRegisterInfo.monthsCount >= 12 && (
                                                                                    <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                                                                                        Eligible
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {whatsappName && (
                                                                <div className="flex items-center gap-1 text-xs font-medium text-emerald-700">
                                                                    <svg
                                                                        className="h-3.5 w-3.5"
                                                                        viewBox="0 0 32 32"
                                                                        aria-hidden="true"
                                                                    >
                                                                        <path
                                                                            fill="currentColor"
                                                                            d="M16 3C9.383 3 4 8.383 4 15c0 2.247.627 4.334 1.72 6.137L4 29l8.137-1.7A11.86 11.86 0 0 0 16 27c6.617 0 12-5.383 12-12S22.617 3 16 3zm0 2c5.533 0 10 4.467 10 10s-4.467 10-10 10a9.8 9.8 0 0 1-3.938-.812l-.28-.118-4.812 1L8.3 20.3l-.145-.238A9.77 9.77 0 0 1 6 15c0-5.533 4.467-10 10-10zm-3.117 4.5c-.26 0-.672.097-1.023.485-.352.39-1.343 1.31-1.343 3.19 0 1.881 1.376 3.695 1.567 3.952.194.26 2.648 4.223 6.543 5.746 3.237 1.272 3.897 1.02 4.593.953.696-.065 2.255-.922 2.574-1.815.32-.89.32-1.653.225-1.815-.096-.162-.352-.258-.735-.452-.384-.195-2.255-1.11-2.604-1.237-.352-.13-.607-.195-.863.194-.257.39-.992 1.237-1.217 1.493-.225.259-.448.293-.83.098-.384-.195-1.62-.597-3.087-1.905-1.142-1.017-1.91-2.272-2.135-2.66-.224-.39-.024-.6.17-.793.175-.173.384-.45.576-.676.19-.227.255-.39.383-.649.129-.26.064-.487-.032-.683-.096-.195-.85-2.082-1.193-2.847-.315-.702-.642-.76-.902-.766z"
                                                                        />
                                                                    </svg>
                                                                    <span>{whatsappName}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <span className="text-sm text-gray-500">No registrations</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Actions */}
                                <div className="grid grid-cols-2 gap-2 w-full md:w-auto md:grid-cols-1">
                                            {!isWithinCoordination && (
                                                <div className="col-span-2 md:col-span-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-700 text-center font-medium">
                                                    Outside your coordination
                                                </div>
                                            )}
                                            {isWithinCoordination && (
                                                <button
                                                    onClick={() => { setSelectedStudent(student); setActiveModal('details'); }}
                                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-slate-300 bg-slate-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    More
                                                </button>
                                            )}
                                            {isWithinCoordination && (
                                                <button
                                                    onClick={() => { setSelectedStudent(student); setActiveModal('print'); }}
                                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                                    </svg>
                                                    Print
                                                </button>
                                            )}
                                            {isWithinCoordination && (
                                                <button
                                                    onClick={() => { setSelectedStudent(student); setActiveModal('image'); }}
                                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                    </svg>
                                                    Image
                                                </button>
                                            )}
                                            <button
                                                onClick={() => { setSelectedStudent(student); setActiveModal('register'); }}
                                                className="inline-flex items-center justify-center gap-1.5 rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                                            >
                                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                                </svg>
                                                Register
                                            </button>
                                            {isWithinCoordination && (
                                                <button
                                                    onClick={() => { setSelectedStudent(student); setActiveModal('update'); }}
                                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-slate-300 bg-slate-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    Update
                                                </button>
                                            )}
                                            {hasFullAccess && (
                                                <button
                                                    onClick={() => { setSelectedStudent(student); setActiveModal('clean'); }}
                                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Clean
                                                </button>
                                            )}
                                </div>
                            </div>
                        </div>
                        );
                    })
                )}
            </div>

            {/* Student Details Modal */}
            {activeModal === 'details' && selectedStudent && (
                <StudentDetailsModal
                    student={{
                        ...selectedStudent,
                        coordinateCourseIds: coordinateCourseIds,
                    }}
                    onClose={closeModal}
                />
            )}

            {/* Print Modal */}
            {activeModal === 'print' && selectedStudent && (
                <PrintModal
                    student={selectedStudent}
                    onClose={closeModal}
                />
            )}

            {/* Image Upload Modal (when clicking "Image" button) */}
            {activeModal === 'image' && selectedStudent && (
                <ImageUploadModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                />
            )}

            {/* Image Lightbox (for viewing full image from thumbnail) */}
            {activeModal === 'view-image' && selectedStudent && (
                <StudentImageLightbox
                    student={selectedStudent}
                    onClose={closeModal}
                    onPrev={showPrevImage}
                    onNext={showNextImage}
                    hasPrev={hasPrevImage}
                    hasNext={hasNextImage}
                />
            )}

            {activeModal === 'add-student' && (
                <AddStudentModal
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                    coordinateCourseIds={coordinateCourseIds}
                    hasFullAccess={hasFullAccess}
                />
            )}

            {/* Register Student Modal */}
            {activeModal === 'register' && selectedStudent && (
                <RegisterStudentModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                />
            )}

            {/* Update Student Modal */}
            {activeModal === 'update' && selectedStudent && (
                <UpdateStudentModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                />
            )}

            {/* Clear Data Modal */}
            {activeModal === 'clean' && selectedStudent && (
                <ClearDataModal
                    student={{
                        ...selectedStudent,
                        coordinateCourseIds: coordinateCourseIds,
                    }}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                    isSuperAdmin={!!AuthService.getUser()?.superAdmin}
                />
            )}

            {/* Reset Password Modal */}
            {activeModal === 'reset password' && selectedStudent && (
                <ResetPasswordModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                    currentUser={user}
                />
            )}

            {/* Reset Parent Password Modal */}
            {activeModal === 'reset parent password' && selectedStudent && (
                <ResetParentPasswordModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                    currentUser={user}
                />
            )}

            {/* Upload Image Modal */}
            {activeModal === 'upload-image' && selectedStudent && (
                <ImageUploadModal
                    student={selectedStudent}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        fetchStudents();
                    }}
                />
            )}

            {/* Quick Edit Status/Delivery Modal (from double-click on badges) */}
            {statusDeliveryModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-lg bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50">
                            <div>
                                <h2 className="text-base font-semibold text-slate-900">Update Registration</h2>
                                <p className="mt-0.5 text-xs text-slate-600">
                                    {statusDeliveryModal.studentName}  b7 {statusDeliveryModal.courseLabel}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeStatusDeliveryModal}
                                className="rounded p-1 text-slate-500 hover:bg-slate-200"
                                disabled={statusDeliverySaving}
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div>
                                <label className="mb-1 block text-xs font-semibold text-slate-700">Registration Status</label>
                                <Select
                                    value={
                                        statusDeliveryModal.registrationStatus
                                            ? { value: statusDeliveryModal.registrationStatus, label: statusDeliveryModal.registrationStatus }
                                            : REGISTRATION_STATUS_OPTIONS[0]
                                    }
                                    onChange={(option) =>
                                        setStatusDeliveryModal((prev) =>
                                            prev
                                                ? { ...prev, registrationStatus: option?.value || '' }
                                                : prev
                                        )
                                    }
                                    options={REGISTRATION_STATUS_OPTIONS}
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '32px',
                                            borderRadius: '0.375rem',
                                            borderColor: '#cbd5f5',
                                            fontSize: '0.875rem',
                                        }),
                                        valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                    }}
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-semibold text-slate-700">Delivery Status</label>
                                <Select
                                    value={
                                        DELIVERY_STATUS_OPTIONS.find(
                                            (opt) => opt.value === (statusDeliveryModal.deliveryStatus || '')
                                        ) || DELIVERY_STATUS_OPTIONS[0]
                                    }
                                    onChange={(option) =>
                                        setStatusDeliveryModal((prev) =>
                                            prev ? { ...prev, deliveryStatus: option?.value || '' } : prev
                                        )
                                    }
                                    options={DELIVERY_STATUS_OPTIONS}
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '32px',
                                            borderRadius: '0.375rem',
                                            borderColor: '#cbd5f5',
                                            fontSize: '0.875rem',
                                        }),
                                        valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
                            <button
                                type="button"
                                onClick={closeStatusDeliveryModal}
                                className="inline-flex items-center rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                disabled={statusDeliverySaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleStatusDeliverySave}
                                className="inline-flex items-center rounded border border-green-600 bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                disabled={statusDeliverySaving}
                            >
                                {statusDeliverySaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {viewHistoryTarget && (
                <RegistrationHistoryModal
                    student={viewHistoryTarget.student}
                    registration={viewHistoryTarget.registration}
                    onClose={() => setViewHistoryTarget(null)}
                    refreshKey={historyRefreshKey}
                    canAddHistory={canModifyRegistration(viewHistoryTarget.registration)}
                />
            )}

            {addHistoryTarget && (
                <AddRegistrationHistoryModal
                    student={addHistoryTarget.student}
                    registration={addHistoryTarget.registration}
                    onClose={() => setAddHistoryTarget(null)}
                    onSuccess={({ registrationId }) => {
                        setAddHistoryTarget(null);
                        handleHistoryAdded(registrationId);
                    }}
                />
            )}

            {/* Other Action Modals - Placeholder for future implementation */}
            {activeModal && !['details', 'image', 'register', 'update', 'clean', 'reset password', 'reset parent password', 'print', 'view-image', 'upload-image'].includes(activeModal) && selectedStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 capitalize">{activeModal} - {selectedStudent.fullName}</h3>
                                <p className="text-sm text-gray-600">Student ID: {selectedStudent.studentId}</p>
                            </div>
                            <button onClick={closeModal} className="rounded-full p-2 text-gray-500 hover:bg-gray-100" aria-label="Close">
                                ✕
                            </button>
                        </div>
                        <div className="mt-4 space-y-2 text-sm text-gray-800">
                            <p>Email: {selectedStudent.email || 'N/A'}</p>
                            <p>Mobile: {selectedStudent.mobileNumber || 'N/A'}</p>
                            <p>Course count: {selectedStudent.registrations?.length || 0}</p>
                            <p className="text-xs text-gray-500">This modal is placeholder for {activeModal} action.</p>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={closeModal}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button
                                onClick={closeModal}
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {!loading && filteredStudents.length > 0 && (
                <div className="mt-6 space-y-4">
                    {/* Results Info + Page Size + Jump to Page */}
                    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 md:flex-row md:items-center md:justify-between">
                        <div className="text-sm text-gray-700">
                            Showing <span className="font-bold text-indigo-600">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> to <span className="font-bold text-indigo-600">{Math.min(pagination.current_page * pagination.per_page, computedTotal)}</span> of <span className="font-bold text-gray-900">{computedTotal}</span> students
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-gray-700">
                            <span className="whitespace-nowrap">Page {pagination.current_page} of {derivedLastPage}</span>
                            
                            {/* Jump to Page */}
                            {derivedLastPage > 1 && (
                                <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 shadow-sm">
                                    <span className="text-xs text-gray-600 whitespace-nowrap">Jump to</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={derivedLastPage}
                                        placeholder={pagination.current_page.toString()}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                const pageNum = parseInt(e.target.value);
                                                if (pageNum >= 1 && pageNum <= derivedLastPage) {
                                                    handlePageChange(pageNum);
                                                    e.target.value = '';
                                                }
                                            }
                                        }}
                                        className="w-16 rounded border border-gray-300 px-2 py-1 text-center text-sm font-semibold text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    <button
                                        onClick={(e) => {
                                            const input = e.target.previousElementSibling;
                                            const pageNum = parseInt(input.value);
                                            if (pageNum >= 1 && pageNum <= derivedLastPage) {
                                                handlePageChange(pageNum);
                                                input.value = '';
                                            }
                                        }}
                                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                                    >
                                        Go
                                    </button>
                                </div>
                            )}
                            
                            {/* Per Page Selector */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600">Per page</span>
                                <select
                                    value={pagination.per_page}
                                    onChange={handlePerPageChange}
                                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-semibold text-gray-800 focus:border-indigo-500 focus:outline-none"
                                >
                                    {[10, 20, 50, 100, 200].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Pagination Buttons */}
                    {derivedLastPage > 1 && (
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                    pagination.current_page === 1
                                        ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                                        : 'bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-300'
                                }`}
                            >
                                Previous
                            </button>

                            {/* Page Numbers */}
                            {(() => {
                                const pages = [];
                                const maxVisiblePages = 7;
                                let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisiblePages / 2));
                                let endPage = Math.min(derivedLastPage, startPage + maxVisiblePages - 1);

                                if (endPage - startPage + 1 < maxVisiblePages) {
                                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                }

                                // First page
                                if (startPage > 1) {
                                    pages.push(
                                        <button
                                            key={1}
                                            onClick={() => handlePageChange(1)}
                                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                                        >
                                            1
                                        </button>
                                    );
                                    if (startPage > 2) {
                                        pages.push(
                                            <span key="ellipsis1" className="px-2 text-gray-500">
                                                ...
                                            </span>
                                        );
                                    }
                                }

                                // Middle pages
                                for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                        <button
                                            key={i}
                                            onClick={() => handlePageChange(i)}
                                            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                                i === pagination.current_page
                                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                                                    : 'border border-gray-300 bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600'
                                            }`}
                                        >
                                            {i}
                                        </button>
                                    );
                                }

                                // Last page
                                if (endPage < derivedLastPage) {
                                    if (endPage < derivedLastPage - 1) {
                                        pages.push(
                                            <span key="ellipsis2" className="px-2 text-gray-500">
                                                ...
                                            </span>
                                        );
                                    }
                                    pages.push(
                                        <button
                                            key={derivedLastPage}
                                            onClick={() => handlePageChange(derivedLastPage)}
                                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                                        >
                                            {derivedLastPage}
                                        </button>
                                    );
                                }

                                return pages;
                            })()}

                            {/* Next Button */}
                            <button
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page === derivedLastPage}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                                    pagination.current_page === derivedLastPage
                                        ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                                        : 'bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-300'
                                }`}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Summary Statistics Modal */}
            {showSummaryModal && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-20">
                    <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b-2 border-gray-300 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="rounded-xl bg-blue-600 p-3 text-white">
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Student Statistics Summary</h3>
                                    <p className="text-sm text-gray-600">Overview of registration and delivery status</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowSummaryModal(false)}
                                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Main Stats */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 p-5 shadow-sm border border-indigo-100">
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-indigo-500 p-3 text-white">
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-600">Total Students (all pages)</p>
                                            <p className="mt-1 text-2xl font-bold text-indigo-600">{pagination.total || students.length}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-5 shadow-sm border border-emerald-100">
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-emerald-500 p-3 text-white">
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-600">Students on this page</p>
                                            <p className="mt-1 text-2xl font-bold text-emerald-600">{filteredStudents.length}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-5 shadow-sm border border-blue-100">
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-lg bg-blue-500 p-3 text-white">
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-600">Available Courses</p>
                                            <p className="mt-1 text-2xl font-bold text-blue-600">{visibleCourses.length}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Breakdown */}
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                {/* Registration Status */}
                                <div className="rounded-xl bg-white p-5 shadow-md border-2 border-indigo-200">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <h3 className="text-base font-bold text-gray-800">Registration Status (This Page)</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                                            <span className="font-semibold text-gray-700 text-sm">Active:</span>
                                            <span className="font-bold text-green-700 text-lg">{registrationStatusCounts.active}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                                            <span className="font-semibold text-gray-700 text-sm">Postponed:</span>
                                            <span className="font-bold text-amber-700 text-lg">{registrationStatusCounts.postponed}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                                            <span className="font-semibold text-gray-700 text-sm">Pending:</span>
                                            <span className="font-bold text-slate-700 text-lg">{registrationStatusCounts.pending}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                                            <span className="font-semibold text-gray-700 text-sm">Suspending:</span>
                                            <span className="font-bold text-orange-700 text-lg">{registrationStatusCounts.suspending}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                                            <span className="font-semibold text-gray-700 text-sm">Suspended:</span>
                                            <span className="font-bold text-red-700 text-lg">{registrationStatusCounts.suspended}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                                            <span className="font-semibold text-gray-700 text-sm">Inactivated:</span>
                                            <span className="font-bold text-gray-700 text-lg">{registrationStatusCounts.inactivated}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery Status */}
                                <div className="rounded-xl bg-white p-5 shadow-md border-2 border-blue-200">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                        <h3 className="text-base font-bold text-gray-800">Delivery Status (This Page)</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                                            <span className="font-semibold text-gray-700 text-sm">Active:</span>
                                            <span className="font-bold text-blue-700 text-lg">{deliveryStatusCounts.active}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-purple-50 border border-purple-200">
                                            <span className="font-semibold text-gray-700 text-sm">Completed:</span>
                                            <span className="font-bold text-purple-700 text-lg">{deliveryStatusCounts['academic delivery completed']}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                            <span className="font-semibold text-gray-700 text-sm">Graduated:</span>
                                            <span className="font-bold text-emerald-700 text-lg">{deliveryStatusCounts.graduated}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-teal-50 border border-teal-200">
                                            <span className="font-semibold text-gray-700 text-sm">Cert. Received:</span>
                                            <span className="font-bold text-teal-700 text-lg">{deliveryStatusCounts['certificate received']}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-cyan-50 border border-cyan-200">
                                            <span className="font-semibold text-gray-700 text-sm">Cert. Dispatched:</span>
                                            <span className="font-bold text-cyan-700 text-lg">{deliveryStatusCounts['certificate dispatched']}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                                            <span className="font-semibold text-gray-700 text-sm">Dropped:</span>
                                            <span className="font-bold text-red-700 text-lg">{deliveryStatusCounts.dropped}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                                            <span className="font-semibold text-gray-700 text-sm">Transfer (Branch):</span>
                                            <span className="font-bold text-yellow-700 text-lg">{deliveryStatusCounts['transfer to another branch']}</span>
                                        </div>
                                        <div className="flex justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                                            <span className="font-semibold text-gray-700 text-sm">Transfer (Course):</span>
                                            <span className="font-bold text-indigo-700 text-lg">{deliveryStatusCounts['transfer to another course']}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 rounded-b-2xl">
                            <button
                                onClick={() => setShowSummaryModal(false)}
                                className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Course Filter Modal */}
            {courseFilterModal && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-20">
                    <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="rounded-xl bg-indigo-600 p-3 text-white">
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Filter Students by Course</h3>
                                    <p className="text-sm text-gray-600">Select one or more courses to view students</p>
                                </div>
                            </div>
                            <button
                                onClick={handleCancelCourseFilter}
                                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                aria-label="Close"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Selection Summary */}
                        <div className="border-b border-gray-200 bg-indigo-50 px-6 py-3">
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold text-gray-900">
                                        {tempSelectedCourses.includes('all')
                                            ? 'All Courses Selected'
                                            : tempSelectedCourses.length === 0
                                            ? 'No Course Selected'
                                            : `${tempSelectedCourses.length} Course${tempSelectedCourses.length === 1 ? '' : 's'} Selected`}
                                    </span>
                                </div>
                                {!hasFullAccess && (
                                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                                        Limited to your coordinated courses
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    value={courseSearchQuery}
                                    onChange={(e) => setCourseSearchQuery(e.target.value)}
                                    placeholder="Search courses by name or schedule type..."
                                    className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                                {courseSearchQuery && (
                                    <button
                                        onClick={() => setCourseSearchQuery('')}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600"
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Course List */}
                        <div className="max-h-96 overflow-y-auto px-6 py-4">
                            <div className="space-y-2">
                                {/* All Courses Option */}
                                <button
                                    onClick={() => handleToggleCourse('all')}
                                    className={`flex w-full items-center gap-3 rounded-lg border-2 p-4 text-left transition-all ${
                                        tempSelectedCourses.includes('all')
                                            ? 'border-indigo-500 bg-indigo-50 shadow-md'
                                            : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50'
                                    }`}
                                >
                                    <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                                        tempSelectedCourses.includes('all')
                                            ? 'border-indigo-500 bg-indigo-500'
                                            : 'border-gray-300 bg-white'
                                    }`}>
                                        {tempSelectedCourses.includes('all') && (
                                            <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">
                                            {hasFullAccess ? 'All Courses' : 'All My Courses'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {hasFullAccess ? 'View students from all courses' : 'View students from all your coordinated courses'}
                                        </div>
                                    </div>
                                </button>

                                {/* Individual Course Options */}
                                {getFilteredCourseOptions().length > 0 ? (
                                    getFilteredCourseOptions().map((course) => {
                                        const isSelected = tempSelectedCourses.includes(course.courseId.toString());
                                        const schedule = course.scheduleType ? String(course.scheduleType).toLowerCase() : '';
                                        const scheduleLabel = schedule === 'weekday' ? 'Weekday' : 'Weekend';
                                        const scheduleBadge = schedule === 'weekday'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-purple-100 text-purple-700';
                                        return (
                                            <button
                                                key={course.courseId}
                                                onClick={() => handleToggleCourse(course.courseId.toString())}
                                                className={`flex w-full items-center gap-3 rounded-lg border-2 p-4 text-left transition-all ${
                                                    isSelected
                                                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                                                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50'
                                                }`}
                                            >
                                                <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                                                    isSelected
                                                        ? 'border-indigo-500 bg-indigo-500'
                                                        : 'border-gray-300 bg-white'
                                                }`}>
                                                    {isSelected && (
                                                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-semibold text-gray-900">{course.fullName}</span>
                                                        {course.scheduleType && (
                                                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${scheduleBadge}`}>
                                                                {schedule === 'weekday' ? 'Weekday' : 'Weekend'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                                        {course.shortName && (
                                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700">{course.shortName}</span>
                                                        )}
                                                        <span className="rounded-full bg-gray-50 px-2 py-0.5 font-semibold text-gray-600">Course ID: {course.courseId}</span>
                                                        {course.scheduleType && (
                                                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700">{scheduleLabel} Schedule</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="py-8 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p className="mt-2 text-sm text-gray-500">No courses found matching your search</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleCancelCourseFilter}
                                    className="rounded-lg border-2 border-gray-300 bg-white px-6 py-2.5 font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApplyCourseFilter}
                                    disabled={tempSelectedCourses.length === 0}
                                    className={`rounded-lg px-6 py-2.5 font-semibold text-white transition-all ${
                                        tempSelectedCourses.length === 0
                                            ? 'cursor-not-allowed bg-gray-300'
                                            : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg'
                                    }`}
                                >
                                    Apply Filter
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
